<template>
	<v-app :class="{ embeded: embed }" :style="layoutStyles">
		<AsyncBackgroundUploader v-if="!embed" />
		<ConfirmDialog ref="confirm"></ConfirmDialog>
		<FileUploadDialog
			ref="fileUploadDialog"
			:shell-header-height="layoutStyles['--shell-header-height']"
			@parked-upload-rearmed="onParkedUploadRearmed"></FileUploadDialog>
		<CreateCustomerDialog ref="createCustomerDialog"></CreateCustomerDialog>
		<SwitchOrganizationDialog
			v-model="showSwitchOrganizationDialog"
			:organizations="organizations()"
			:current-organization-id="currentOrganizationId" />

		<NoAccount ref="noAccount"></NoAccount>
		<Notification ref="notification"></Notification>
		<FreeTrialEndedDialog ref="freeTrialEndedDialog" @upgrade="upgrade()"></FreeTrialEndedDialog>
		<WelcomeWizardDialog :visible="showWelcomeWizard" :organization="me?.organization || null" @completed="onWelcomeWizardCompleted" />
		<!-- LSP-22421: "the Home Screen web app is being retired, get the LiveSwitch app" — iOS Home Screen
		     launches only, kill switch on /me config.featureFlags.ios_home_screen_deprecation_warning.
		     Held back while the welcome wizard is up so two persistent dialogs never stack. -->
		<IosHomeScreenDeprecationDialog :me="me" :suppressed="showWelcomeWizard" />
		<div v-if="showTrialBar || showExchangeAuthHealthBar || showMobileAppBanner" ref="shellBanners" class="shell-banners no-print">
			<!-- Open/Get banner for the LiveSwitch mobile app: signed-in employees in a phone browser on the
			     home page and the Start a Recording / Take Photos / Upload Photos pages. The component decides browser
			     eligibility (phone UA, not the Home Screen web app) and reports it back so the shell offset
			     only counts a banner that rendered. -->
			<MobileAppBanner
				v-if="showMobileAppBanner"
				:account="me?.account"
				:surface="mobileAppBannerSurface"
				@visible="mobileAppBannerRendered = $event"
				@dismiss="mobileAppBannerDismissed = true" />
			<TrialTopBar
				v-if="showTrialBar"
				ref="trialTopBar"
				:is-ended-trial="isEndedTrial"
				:trial-days-remaining="trialDaysRemaining"
				:is-trial-c-c-now="isTrialCCNow"
				@upgrade="upgrade()">
			</TrialTopBar>
			<ExchangeAuthHealthTopBar v-if="showExchangeAuthHealthBar" @show-all="showExchangeAuthHealthDialog = true" />
		</div>
		<ExchangeAuthHealthDialog
			ref="exchangeAuthHealthDialog"
			v-model:visible="showExchangeAuthHealthDialog"
			:platforms="authHealthFailingPlatforms"
			:can-reconnect="isSettingsAdmin"
			@reconnect="handleReconnect" />

		<v-alert
			v-if="sessionLostBannerVisible"
			ref="sessionLostBanner"
			v-test="'session-lost-banner'"
			type="warning"
			variant="tonal"
			border="start"
			class="session-lost-banner ma-3 no-print"
			rounded="lg"
			closable
			@click:close="sessionLostBannerDismissed = true">
			<template #title>We can't read your session</template>
			Your sign-in info looks incomplete or out of date. Please sign in again so we can keep things secure.
			<template #append>
				<v-btn v-test="'session-lost-banner-reauth'" variant="text" color="warning" @click="reauth">Sign in Again</v-btn>
			</template>
		</v-alert>

		<v-snackbar
			v-model="maintenanceInformation.visible"
			:color="maintenanceInformation.color"
			:multi-line="maintenanceInformation.mode === 'multi-line'"
			:timeout="maintenanceInformation.timeout"
			:location="maintenanceInformation.position"
			class="maintenance counter-v-application-top">
			<div class="maintenance-content">
				<v-btn
					v-if="maintenanceInformation.timeout === -1"
					class="maintenance-close"
					icon="icon-close-medium"
					variant="plain"
					slim
					@click="maintenanceInformation.visible = false"></v-btn>
				<h3 v-if="maintenanceInformation.title">
					{{ maintenanceInformation.title }}
				</h3>
				<p v-if="maintenanceInformation.text">
					{{ maintenanceInformation.text }}
				</p>
			</div>
		</v-snackbar>

		<NewVersionDialog v-model="updateAvailable" />

		<v-snackbar
			v-model="pluginInstallation.visible"
			:color="pluginInstallation.color"
			:multi-line="pluginInstallation.mode === 'multi-line'"
			:timeout="pluginInstallation.timeout"
			:location="pluginInstallation.position">
			<!-- LSP-15861: the actions live in their own row below the message on xs and collapse
			     back into a single row from sm up (desktop layout unchanged). They cannot share the
			     message's flex row on a phone: v-btn is white-space: nowrap and .v-snackbar__wrapper
			     has min-width: 344px, so the message column becomes the only shrinkable box and
			     collapses to roughly one word per line. -->
			<div class="d-flex flex-column flex-sm-row align-sm-center ga-2">
				<div class="d-flex align-center ga-2">
					<v-icon class="flex-shrink-0" size="xxx-large">{{ pluginInstallation.icon }}</v-icon>
					<!-- text-break: this text is dynamic (a plugin display name, or a provider's raw
					     error_description on the failure toast) and can contain a long unbroken token.
					     Without break-word such a token sets the flex item's min-content width and
					     overflows the snackbar instead of wrapping — worst on the narrow screens this
					     change is about. -->
					<div class="d-flex flex-column text-break">
						<div>
							<strong>{{ pluginInstallation.title }}</strong>
						</div>
						<div>
							{{ pluginInstallation.text }}
							<br />
							<span v-if="pluginInstallation.showAction">
								<a
									style="color: white; text-decoration: underline"
									:href="pluginInstallation.href"
									@click.stop.prevent="connectAccount(pluginInstallation.href)"
									>Connect your account</a
								>
								to start sending messages.
							</span>
						</div>
					</div>
				</div>
				<div v-if="showPluginInstallActions" class="d-flex align-center justify-end ga-2 flex-shrink-0">
					<!-- LSP-15861: "Don't show again" renders only for the FIRST-TIME prompt (banner
					     state still null). The once-per-session reminder on Integrations fires with
					     state "dismissed"/"acknowledged" and must not offer the button — clicking it
					     there would write "dismissed" over an "acknowledged" state and resurrect the
					     sidebar badge the user just cleared by visiting the page. -->
					<v-btn
						v-if="showPluginInstallDismissForever"
						v-test="'plugin-install-banner-dont-show-again'"
						variant="text"
						color="white"
						slim
						@click="dismissPluginInstallBannerForever">
						Don't show again
					</v-btn>
					<v-btn
						icon="icon-close-medium"
						v-if="showPluginInstallClose"
						@click="pluginInstallation.visible = false"
						variant="plain"
						slim>
					</v-btn>
				</div>
			</div>
		</v-snackbar>

		<v-app-bar
			v-if="appBar && !isPublicUnauthedView"
			ref="appTopBar"
			v-test="'app-top-bar'"
			absolute
			:height="searchAppBarHeight"
			:class="{ 'app-top-bar-expanded': searchAppBarHeight > 64 }"
			elevation="0"
			color="white"
			class="pl-2 pr-2 app-top-bar no-print">
			<div class="app-top-bar-brand mr-2">
				<a v-if="display.mdAndUp.value && !display.mobile.value" href="/" aria-label="LiveSwitch" v-test="'home-link-desktop'"
					><img style="height: 36px" src="./assets/ls-contact-3d-color-logo.svg" alt="" aria-hidden="true"
				/></a>
				<a v-if="display.smAndDown.value || display.mobile.value" href="/" aria-label="LiveSwitch" v-test="'home-link-mobile'"
					><img style="height: 36px" src="./assets/ls-contact-bubbles.svg" alt="" aria-hidden="true"
				/></a>
			</div>

			<div class="d-flex ga-2 ml-auto app-top-bar-actions" :class="searchAppBarHeight > 64 ? 'align-start' : 'align-center'">
				<SearchBar v-if="isAuthenticated" ref="globalSearch" class="search-bar" />

				<!-- <v-icon v-if="isAuthenticated" @click="getNotifications">icon-bell-stroke</v-icon> -->

				<v-menu
					v-model="showNotification"
					@update:modelValue="onNotificationMenuUpdate"
					transition="slide-y-transition"
					location-strategy="connected"
					location="bottom end"
					origin="top end"
					:offset="[0, 8]"
					:min-width="menuMinWidth"
					:max-width="notificationMenuMaxWidth"
					content-class="profile-menu">
					<template #activator="{ props }">
						<v-btn
							v-if="isAuthenticated"
							v-test="'notifications'"
							v-track="'notifications-button'"
							:aria-label="unreadNotificationCount > 0 ? `Notifications, ${unreadNotificationCount} unread` : 'Notifications'"
							:disabled="notifications.length == 0 && !loadingNotifications"
							:aria-busy="loadingNotifications"
							icon
							density="compact"
							v-bind="props">
							<v-badge
								color="#02B555"
								location="top end"
								:max="99"
								:offset-x="8"
								:content="unreadNotificationCount"
								:model-value="unreadNotificationCount > 0">
								<v-icon>icon-bell-stroke</v-icon>
							</v-badge>
						</v-btn>
					</template>

					<v-list density="compact" class="notifications-dropdown pb-0">
						<v-list-item>
							<div class="font-weight-bold text-subtitle-1" style="width: 100%">
								Notifications <span v-if="totalNotifications > 0">({{ totalNotifications }})</span>
							</div>
						</v-list-item>
						<div v-if="loadingNotifications" class="d-flex flex-column align-center justify-center pa-4 ga-2">
							<v-progress-circular size="64" indeterminate color="primary"></v-progress-circular>
							<div class="text-subtitle-1">Loading notifications...</div>
						</div>
						<template v-else>
							<!-- Display the first four notifications -->
							<v-list-item
								v-for="(item, i) in notifications.filter((n) => n.metadata).slice(0, 5)"
								:key="i"
								class="d-flex full-content"
								:class="{ 'unread-row': !item.isRead }"
								@click="() => clickNotificationAndUpdate(item)">
								<div class="py-1 px-1">
									<div class="d-flex align-center justify-start w-100 ga-1">
										<v-icon size="24" color="black">{{ getNotificationIcon(item) }}</v-icon>
										<div
											:title="getNotificationTitle(item)"
											class="text-subtitle-2 flex-grow-1 text-truncate max-width-300">
											{{ getNotificationTitle(item) }}
										</div>
										<div>
											<v-icon
												v-if="!item.isRead"
												size="20"
												class="read-icon"
												@click.stop="() => readNotification(item)">
												icon-check-heavy
											</v-icon>
										</div>
									</div>
									<div class="pl-2 text-caption">{{ getNotificationTime(item) }}</div>
								</div>
							</v-list-item>
							<v-list-item v-if="notifications.length > 5" class="d-flex">
								<div class="text-left pl-2">
									<router-link to="/notifications" id="more-notifications-link"
										>... {{ totalNotifications - 5 }} more notification{{
											totalNotifications > 6 ? "s" : ""
										}}</router-link
									>
								</div>
							</v-list-item>
							<v-divider class="divider"></v-divider>
							<v-list-item class="pa-0 ma-0" style="background-color: transparent !important">
								<v-list-item-action class="d-flex pa-0 flex-nowrap justify-space-between">
									<v-btn
										class="flex-grow-1"
										variant="text"
										size="large"
										:color="unreadNotificationCount === 0 ? 'grey darken-2' : 'blue darken-2'"
										:disabled="unreadNotificationCount === 0"
										@click="clearAllNotifications">
										Mark all as read
									</v-btn>
									<v-divider vertical></v-divider>
									<v-btn
										variant="text"
										class="flex-grow-1"
										color="blue darken-2"
										size="large"
										@click="goToNotificationsPage">
										See All
									</v-btn>
								</v-list-item-action>
							</v-list-item>
						</template>
					</v-list>
				</v-menu>

				<v-menu v-if="navigationReady && !showLeftNav()" min-width="250" max-width="250" location="bottom">
					<template #activator="{ props }">
						<v-btn v-test="'app-bar-help'" class="ml-2" icon density="compact" aria-label="Help" v-bind="props">
							<v-icon size="24">icon-circle-help-stroke</v-icon>
						</v-btn>
					</template>
					<v-list density="compact" rounded="lg" class="help-menu-list">
						<v-list-item
							v-test="'side-nav-contact-support'"
							v-track="'side-nav-contact-support-button'"
							@click="openContactSupportDialog">
							<template #prepend>
								<v-icon>icon-envelope-stroke</v-icon>
							</template>
							<v-list-item-title>Contact Support</v-list-item-title>
						</v-list-item>
						<v-list-item
							v-test="'side-nav-getting-started-guide'"
							v-track="'side-nav-getting-started-guide-button'"
							@click="openGettingStartedGuide">
							<template #prepend>
								<v-icon>icon-open-book-stroke</v-icon>
							</template>
							<v-list-item-title>Help Guide</v-list-item-title>
						</v-list-item>
						<v-list-item v-test="'side-nav-coaching-videos'" v-track="'side-nav-coaching-videos-button'" to="/videos">
							<template #prepend>
								<v-icon>icon-video-play-stroke</v-icon>
							</template>
							<v-list-item-title>Coaching Videos</v-list-item-title>
						</v-list-item>
						<v-list-item
							v-test="'side-nav-share-your-feedback'"
							v-track="'side-nav-share-your-feedback-button'"
							@click="showFeedbackDialog = true">
							<template #prepend>
								<v-icon>icon-lightbulb-stroke</v-icon>
							</template>
							<v-list-item-title>Share Your Feedback</v-list-item-title>
						</v-list-item>
						<v-list-item
							v-test="`side-nav-refer-a-friend`"
							v-track="`side-nav-refer-a-friend-button`"
							@click="showReferralPage">
							<template #prepend>
								<v-icon>icon-user-rotation</v-icon>
							</template>
							<v-list-item-title class="text-wrap">Refer a Friend!</v-list-item-title>
						</v-list-item>
						<v-list-item
							v-test="'side-nav-data-privacy'"
							v-track="'side-nav-data-privacy-button'"
							@click="openDataPrivacyDialog">
							<template #prepend>
								<v-icon>icon-check-shield</v-icon>
							</template>
							<v-list-item-title>Data Privacy</v-list-item-title>
						</v-list-item>
						<v-list-item
							v-if="whatsNewEnabled()"
							v-test="'side-nav-whats-new'"
							v-track="'side-nav-whats-new-button'"
							to="/whats-new">
							<template #prepend>
								<v-icon>icon-news-stroke</v-icon>
							</template>
							<v-list-item-title>What's New</v-list-item-title>
						</v-list-item>
						<v-list-item
							v-test="'side-nav-whats-coming-new'"
							v-track="'side-nav-whats-coming-new-button'"
							@click="showHeresWhatsComingPage">
							<template #prepend>
								<v-icon>icon-heart-1-solid</v-icon>
							</template>
							<v-list-item-title class="text-wrap" style="hyphens: none">
								Here's what's coming in the product!
								<span class="banner-text tab-text">NEW!</span>
							</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>

				<v-btn
					v-if="allowProjectPage"
					v-test="'sign-in'"
					v-track="'sign-in-button'"
					rounded="xl"
					elevation="0"
					variant="tonal"
					color="primary"
					@click="redirectToHome"
					>Sign In</v-btn
				>
				<v-menu
					v-if="isAuthenticated"
					v-model="profileShown"
					transition="slide-y-transition"
					location="bottom"
					width="250"
					content-class="profile-menu">
					<template #activator="{ props }">
						<button
							v-test="'profile-menu'"
							v-track="'profile-menu'"
							v-bind="props"
							type="button"
							class="d-flex align-center ml-2 profile-activator"
							:aria-label="`User switcher, ${displayName}, ${organizationName}`">
							<v-avatar
								:size="mini ? 28 : 42"
								:color="getUserAvatarBackgroundColor(me)"
								v-test="'profile-avatar'"
								class="profile-top-avatar">
								<span class="team-avatar-text profile-top-avatar-text" :style="{ color: getUserAvatarTextColor() }">{{
									userInitials
								}}</span>
							</v-avatar>
							<div v-if="!mini" class="profile-summary ml-2">
								<div class="profile-summary-name text-truncate">{{ displayName }}</div>
								<div class="profile-summary-org text-truncate">{{ organizationName }}</div>
							</div>
							<v-icon v-if="!mini" v-test="'profile-expand'" class="ml-1 profile-expand-icon">
								{{ profileShown ? "icon-chevron-up" : "icon-chevron-down" }}
							</v-icon>
						</button>
					</template>
					<v-list nav class="profile-list pa-4">
						<v-list-item class="profile-user-details ma-0">
							<template #prepend>
								<v-avatar :color="getUserAvatarBackgroundColor(me)"
									><span class="team-avatar-text" :style="{ color: getUserAvatarTextColor() }">{{
										userInitials
									}}</span></v-avatar
								>
							</template>
							<div>
								<h6 class="text-truncate">
									{{ displayName }}
								</h6>
								<div class="text-truncate text-caption">
									{{ organizationName }}
								</div>
							</div>
						</v-list-item>
						<v-divider></v-divider>
						<div v-for="item in profileItems" :key="item.id" v-test="'profile-menu-item-' + item.text">
							<template v-if="item.disabled?.()">
								<v-tooltip location="top">
									<template #activator="{ props }">
										<div v-bind="props">
											<v-list-item class="not-allowed-profile-item pa-0 text-secondary">
												<template #prepend>
													<v-icon>{{ item.icon }}</v-icon>
												</template>
												<v-list-item-title>{{ item.text }}</v-list-item-title>
											</v-list-item>
										</div>
									</template>
									<span>{{ item.disabledTooltip }}</span>
								</v-tooltip>
							</template>
							<v-list-item
								v-else
								v-track="'profile-menu-item-' + item.text + '-button'"
								class="ma-0 text-secondary"
								:class="{ 'nav-item-locked': item.locked?.() }"
								@click="profileItemClick(item)">
								<template #prepend>
									<v-icon :size="item.iconSize">{{ item.icon }}</v-icon>
								</template>
								<v-list-item-title>{{ item.text }}</v-list-item-title>
							</v-list-item>
						</div>
						<v-divider></v-divider>
						<v-list-item v-test="'sign-out'" v-track="'sign-out-button'" class="ma-0 text-primary" @click="logout">
							<template #prepend>
								<v-icon color="primary">icon-door-open</v-icon>
							</template>
							<v-list-item-title>Sign Out</v-list-item-title>
						</v-list-item>
						<v-divider></v-divider>
						<div v-test="'privacy-policy'" v-track="'privacy-policy-button'" class="menu-footer-links">
							<v-btn elevation="0" @click="showPrivacyPolicy"> Privacy Policy </v-btn>
						</div>
					</v-list>
				</v-menu>
			</div>
		</v-app-bar>

		<v-navigation-drawer
			v-if="navigation && isAuthenticated && navigationReady && showLeftNav()"
			v-model="navigation"
			data-test-id="side-nav-shell"
			class="primary-nav no-print"
			app
			permanent
			touchless
			floating
			:rail="mini"
			width="200">
			<v-list density="compact" nav color="primary" v-click-outside="closeHelpMenu">
				<v-list-item
					v-for="item in leftItems"
					:key="item.id"
					:active="navItemActive(item)"
					:class="[`nav-item-${item.id}`, { 'v-list-item--active': navItemActive(item), 'nav-item-locked': item.locked?.() }]"
					:to="navItemTo(item)"
					prepend-gap="8px"
					v-test="`side-nav-${item.testLabel || item.text}`"
					v-track="`side-nav-${item.testLabel || item.text}-button`"
					@click="navItemClick(item)">
					<template #prepend>
						<v-icon v-if="item.icon" :size="item.iconSize">{{ item.icon }}</v-icon>
						<img v-else-if="item.svg" :src="item.svg" width="42" height="18" />
					</template>
					<v-list-item-title class="v-list-item-title">
						<div v-if="item.subtext" class="nav-item-with-subtext">
							<div class="d-flex align-center">
								<div class="d-flex flex-column">
									<div class="nav-main-text">{{ item.text }}</div>
									<div class="nav-main-text font-weight-bold">• {{ item.subtext }}</div>
								</div>
								<div>
									<span v-if="item.bannerText" class="banner-text tab-text nav-banner-centered">{{
										item.bannerText
									}}</span>
								</div>
							</div>
							<!-- <div class="nav-main-text">
									{{ item.text }}
									<span class="banner-text tab-text nav-banner-centered" v-if="item.bannerText">{{ item.bannerText }}</span>
								</div>
								<div class="">{{ item.subtext }}</div> -->
						</div>
						<div v-else class="nav-item-single">
							<span>{{ item.text }}</span>
							<span v-if="item.bannerText" class="banner-text tab-text">{{ item.bannerText }}</span>
						</div>
					</v-list-item-title>
				</v-list-item>
				<v-divider aria-hidden="true" />
				<v-list-item
					v-test="`side-nav-help`"
					v-track="`side-nav-help-button`"
					prepend-gap="8px"
					class="help-menu-header"
					:class="{ 'v-list-item--active': helpMenuOpen }"
					role="button"
					:aria-expanded="helpMenuOpen.toString()"
					@mousedown.prevent
					@click="helpMenuOpen = !helpMenuOpen">
					<template #prepend>
						<v-icon>icon-circle-help-stroke</v-icon>
					</template>
					<template #append>
						<v-icon>{{ helpMenuOpen ? "icon-chevron-up" : "icon-chevron-down" }}</v-icon>
					</template>
					<v-list-item-title class="d-flex align-center justify-space-between">Help</v-list-item-title>
				</v-list-item>
				<template v-if="helpMenuOpen">
					<v-list-item
						class="pl-4 help-menu-item"
						prepend-gap="8px"
						v-test="`side-nav-contact-support`"
						v-track="`side-nav-contact-support-button`"
						@mousedown.prevent
						@click="
							openContactSupportDialog();
							helpMenuOpen = false;
						">
						<template #prepend>
							<v-icon>icon-envelope-stroke</v-icon>
						</template>
						<v-list-item-title>Contact Support</v-list-item-title>
					</v-list-item>
					<v-list-item
						class="pl-4 help-menu-item"
						prepend-gap="8px"
						v-test="`side-nav-getting-started-guide`"
						v-track="`side-nav-getting-started-guide-button`"
						@mousedown.prevent
						@click="
							openGettingStartedGuide();
							helpMenuOpen = false;
						">
						<template #prepend>
							<v-icon>icon-open-book-stroke</v-icon>
						</template>
						<v-list-item-title>Help Guide</v-list-item-title>
					</v-list-item>
					<v-list-item
						class="pl-4 help-menu-item"
						prepend-gap="8px"
						v-test="`side-nav-user-suggestions`"
						v-track="`side-nav-user-suggestions-button`"
						@mousedown.prevent
						@click="
							showFeedbackDialog = true;
							helpMenuOpen = false;
						">
						<template #prepend>
							<v-icon>icon-lightbulb-stroke</v-icon>
						</template>
						<v-list-item-title>User Suggestions</v-list-item-title>
					</v-list-item>
					<v-list-item
						class="pl-4 help-menu-item"
						prepend-gap="8px"
						v-test="`side-nav-refer-a-friend`"
						v-track="`side-nav-refer-a-friend-button`"
						@mousedown.prevent
						@click="
							showReferralPage();
							helpMenuOpen = false;
						">
						<template #prepend>
							<v-icon>icon-user-rotation</v-icon>
						</template>
						<v-list-item-title class="text-wrap">Refer a Friend!</v-list-item-title>
					</v-list-item>
					<v-list-item
						class="pl-4 help-menu-item"
						prepend-gap="8px"
						v-test="'side-nav-data-privacy'"
						v-track="'side-nav-data-privacy-button'"
						@mousedown.prevent
						@click="
							openDataPrivacyDialog();
							helpMenuOpen = false;
						">
						<template #prepend>
							<v-icon>icon-check-shield</v-icon>
						</template>
						<v-list-item-title>Data Privacy</v-list-item-title>
					</v-list-item>
					<v-list-item
						v-if="whatsNewEnabled()"
						v-test="'side-nav-whats-new'"
						v-track="'side-nav-whats-new-button'"
						class="pl-4 help-menu-item"
						prepend-gap="8px"
						to="/whats-new"
						@mousedown.prevent
						@click="helpMenuOpen = false">
						<template #prepend>
							<v-icon>icon-news-stroke</v-icon>
						</template>
						<v-list-item-title>What's New</v-list-item-title>
					</v-list-item>
					<v-list-item
						class="pl-4 py-3 help-menu-item"
						prepend-gap="8px"
						v-test="'side-nav-whats-coming-new'"
						v-track="'side-nav-whats-coming-new-button'"
						@mousedown.prevent
						@click="
							showHeresWhatsComingPage();
							helpMenuOpen = false;
						">
						<template #prepend>
							<v-icon>icon-heart-1-solid</v-icon>
						</template>
						<v-list-item-title class="text-wrap" style="hyphens: none">
							Here's what's coming in the product!
							<span class="banner-text tab-text">NEW!</span>
						</v-list-item-title>
					</v-list-item>
				</template>
			</v-list>
		</v-navigation-drawer>

		<router-view v-if="attributesEnabled() && !bootErrorMessage" :key="forceRerenderKey" name="inner" />

		<v-bottom-navigation
			v-if="navigation && isAuthenticated && navigationReady && !bootErrorMessage && showBottomNav()"
			v-test="'bottom-nav-shell'"
			:model-value="currentActiveUrl"
			app
			grow>
			<v-btn
				v-for="item in bottomItems"
				:key="item.id"
				:value="resolveItemTo(item)"
				:to="resolveItemTo(item)"
				:active="isNavItemActive(item)"
				v-test="`bottom-nav-${item.testLabel || item.text}`"
				v-track="`bottom-nav-${item.testLabel || item.text}`"
				color="transparent">
				<v-icon>{{ item.mobileIcon }}</v-icon>

				<span>{{ item.text }}</span>
			</v-btn>
		</v-bottom-navigation>

		<v-main class="main">
			<BlockedUser v-if="blocked" />
			<EntityDetailPage v-else-if="allowProjectPage" :project-id="projectId" :share-id="shareId" />
			<!-- Keep the single-use claim route in one branch while the async auth check
			     changes isAuthenticated; moving it between router-views remounts the page. -->
			<router-view v-else-if="isShareClaimView" :key="$route.path"></router-view>
			<router-view v-else-if="isPublicUnauthedView" :key="$route.path"></router-view>
			<LoadingPage v-else-if="!isAuthenticated || bootErrorMessage" :message="bootErrorMessage" />

			<v-container
				v-if="!allowProjectPage && !isShareClaimView && !isPublicUnauthedView"
				class="app-container align-stretch shell fill-height"
				:class="{
					'no-padding': noPaddingRequired,
					'bottom-back-buttons': areButtonsSticky,
					'mobile-container-app': display.mobile.value
				}"
				fluid>
				<router-view v-if="isAuthenticated && !bootErrorMessage" :key="$route.path + forceRerenderKey"></router-view>
			</v-container>

			<ContactSupportDialog ref="contactSupportDialog"></ContactSupportDialog>
			<DataPrivacyDialog ref="dataPrivacyDialog"></DataPrivacyDialog>
			<CustomerFeedbackDialog
				:dialog-visible="showFeedbackDialog"
				@cancel="showFeedbackDialog = false"
				@feedbackSent="showFeedbackDialog = false" />
			<ReferAFriendDialog
				:dialog-visible="showReferralDialog"
				@cancel="showReferralDialog = false"
				@referralSent="showReferralDialog = false" />
		</v-main>
	</v-app>
</template>

<script>
	import { provide, useTemplateRef } from "vue";
	import { useOrgRouteGuard } from "./composables/useOrgRouteGuard";
	import { useAuthSessionStore } from "./stores/authSession";
	import { useOrgSwitch } from "./composables/useOrgSwitch";
	import { createLatestRequestScope } from "./composables/useLatestRequest";
	import { isNetworkShapedBootAccountLoadError, retryDeferredBootAccountNavigation } from "./router";
	import AsyncBackgroundUploader from "./components/AsyncBackgroundUploader.vue";
	import FileUploadDialog from "./components/FileUploadDialog.vue";
	import LoadingPage from "./components/LoadingPage";
	import EntityDetailPage from "./components/EntityDetailPage.vue";
	import BlockedUser from "./components/BlockedUser";
	import TrialTopBar from "./components/TrialTopBar.vue";
	import MobileAppBanner from "./components/MobileAppBanner.vue";
	import { clearAppcuesIdentity, getAppcuesIdentifyDecision } from "./helpers/appcuesIdentifyPolicy";
	import { mobileAppBannerSurfaceForRoute } from "./helpers/mobileAppBannerSurface";
	import ExchangeAuthHealthTopBar from "./components/ExchangeAuthHealthTopBar.vue";
	import ExchangeAuthHealthDialog from "./components/ExchangeAuthHealthDialog.vue";
	import api from "./api";
	import { isMeFeatureFlagEnabled } from "./helpers/meFeatureFlags";
	import { getApps, getAuthHealth, watchConnected } from "@/api/exchange";
	import { resolveReconnectBannerPlatforms } from "@/helpers/exchangeAuthHealth";
	import { normalizeLegacyRoutePath } from "@/helpers/legacyRoutePaths";
	import parser from "./helpers/parser";
	import Account from "./api/account";
	import DeviceUtility from "./helpers/device";
	import { EventBus } from "./helpers/eventBus";
	import FreeTrialEndedDialog from "./components/FreeTrialEndedDialog.vue";
	import WelcomeWizardDialog from "./components/WelcomeWizardDialog.vue";
	import IosHomeScreenDeprecationDialog from "./components/IosHomeScreenDeprecationDialog.vue";
	import { hasFreeTrialEnded } from "./helpers/hasFreeTrialEnded";
	import { constants } from "./api/constants";
	import { isNewSubscription, effectiveTrialDaysRemaining } from "./helpers/subscription";
	import { isRoleOrInRoles } from "./api/account";
	import { Role } from "./enums/Role";
	import StorageWrapper from "@/helpers/storage";
	import Socket from "@/plugins/socket";
	import CreateCustomerDialog from "./components/CreateCustomerDialog.vue";
	import ConfirmDialog from "./components/ConfirmDialog.vue";
	import SwitchOrganizationDialog from "./components/SwitchOrganizationDialog.vue";
	import SearchBar from "./components/search/SearchBar.vue";
	import {
		getNotificationTitle,
		getNotificationIcon,
		getNotificationTime,
		readNotification,
		clickNotificationMixin
	} from "@/helpers/notifications";
	import ContactSupportDialog from "./components/ContactSupportDialog.vue";
	import CustomerFeedbackDialog from "./components/CustomerFeedbackDialog.vue";
	import ReferAFriendDialog from "./components/ReferAFriendDialog.vue";
	import DataPrivacyDialog from "./components/DataPrivacyDialog.vue";
	import NoAccount from "./components/NoAccount.vue";
	import Notification from "./components/Notification.vue";
	import NewVersionDialog from "./components/NewVersionDialog.vue";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import { useRolesStore } from "./stores/roles";
	import { fileUploadDialogKey } from "@/types/keys";
	import { useVersionCheck } from "@/composables/useVersionCheck";
	import {
		getPendingFileUploadSessions,
		resetUploadingSessions,
		hasCurrentPageInitiatedFileUpload,
		drainCancelledUploadSessions,
		IdbStallError,
		isIdbInternalError
	} from "@/db/fileUploadDb";
	import { logStorageEstimate, UploadCancelledError } from "@/services/chunkedUploadService";
	import { calculateTimeout, getReadableInitialsColor } from "@/helpers/utilities";
	import { isWhatsNewEnabled } from "@/helpers/isWhatsNewEnabled";
	import { describeShareIdSegmentForLog, parsePathFormShareLink, parseShareLinkLocation } from "@/helpers/shareLink";
	import { isReportsEnabled } from "@/helpers/isReportsEnabled";
	import { logError, logInfo, logWarn } from "@/helpers/logError";
	import { isUnauthenticatedError } from "@/types/errors/UnauthenticatedError";
	import {
		clearPendingOrgSwitchFor,
		consumePendingOrgSwitch,
		normalizeOrganizationId,
		readPendingOrgSwitchId
	} from "@/helpers/pendingOrgSwitch";
	import { isTransientAuthTokenError } from "@/types/errors/TransientAuthTokenError";
	import { resumeAdminMultipartSession } from "@/services/uploads/adminResumeSession";
	import { createAdminUploadDrain } from "@/services/uploads/adminUploadDrain";
	import { getAdminSmallFileUploadLane, resetAdminSmallFileUploadLaneForTests } from "@/services/uploads/adminSmallFileUploadLane";
	import { couldNot, genericFailure, POPUP_BLOCKED } from "@/helpers/userMessages";
	import {
		getLandscapeViewportBaseline,
		isLandscapeKeyboardVisible,
		shouldTrustLandscapeViewportBaseline
	} from "./helpers/landscapeKeyboard";

	const APPCUES_SETTLE_TIMEOUT_MS = 2000;

	// Returns true if the vendor call settled successfully, false if it did not settle within the
	// timeout. Early rejections reach the caller's catch; late rejections are reported here.
	const settleAppcuesCall = (result, callName) => {
		if (!result || typeof result.then !== "function") {
			return Promise.resolve(true);
		}
		let timeoutHandle;
		let timedOut = false;
		const vendorCall = Promise.resolve(result);
		vendorCall.catch((err) => {
			if (timedOut) {
				console.error("Error initializing AppCues (" + callName + ")", err);
			}
		});
		const timeoutResult = new Promise((resolve) => {
			timeoutHandle = setTimeout(() => {
				timedOut = true;
				console.info(
					"AppCues " +
						callName +
						" did not settle within " +
						APPCUES_SETTLE_TIMEOUT_MS +
						"ms; outcome unknown, so initialization is not being claimed."
				);
				resolve(false);
			}, APPCUES_SETTLE_TIMEOUT_MS);
		});
		return Promise.race([vendorCall.then(() => true), timeoutResult]).finally(() => clearTimeout(timeoutHandle));
	};

	export default {
		name: "App",
		components: {
			AsyncBackgroundUploader,
			FileUploadDialog,
			LoadingPage,
			EntityDetailPage,
			BlockedUser,
			TrialTopBar,
			MobileAppBanner,
			ExchangeAuthHealthTopBar,
			ExchangeAuthHealthDialog,
			FreeTrialEndedDialog,
			WelcomeWizardDialog,
			IosHomeScreenDeprecationDialog,
			CreateCustomerDialog,
			ConfirmDialog,
			SwitchOrganizationDialog,
			ContactSupportDialog,
			CustomerFeedbackDialog,
			ReferAFriendDialog,
			DataPrivacyDialog,
			SearchBar,
			NoAccount,
			Notification,
			NewVersionDialog
		},
		setup() {
			const fileUploadDialog = useTemplateRef("fileUploadDialog");
			provide(fileUploadDialogKey, fileUploadDialog);

			const display = useDisplay();
			const toast = useToast();
			const rolesStore = useRolesStore();
			const authSession = useAuthSessionStore();
			const orgSwitch = useOrgSwitch();
			// Drops stale resolutions from refreshCurrentUserContextForOrgSwitch
			// when a user triggers two rapid switches (A→B then B→C). Without
			// this, an out-of-order refreshMe could overwrite this.me with the
			// previous org's data while the active token is for the next org —
			// scribe + socket would then see mismatched state.
			const orgSwitchRequestScope = createLatestRequestScope();
			const { updateAvailable } = useVersionCheck();
			return { display, toast, rolesStore, authSession, orgSwitch, orgSwitchRequestScope, updateAvailable };
		},
		mixins: [clickNotificationMixin],
		data: () => {
			const params = new Proxy(new URLSearchParams(window.location.search), {
				get: (searchParams, prop) => searchParams.get(prop)
			});
			const isInIframe = window.self !== window.top;
			const embed = params.embed == "true" && isInIframe;
			return {
				loading: true,
				bootErrorMessage: "",
				loadingNotifications: false,
				_uploadStartupReclaimDone: false,
				// Session-lost banner: shows whenever authSession.sessionLost is true,
				// unless the user has explicitly dismissed it via the X. The dismissal
				// is reset whenever sessionLost transitions false → true so a new
				// session-loss event re-shows the banner.
				sessionLostBannerDismissed: false,
				// Mobile app Open/Get banner (LSP-21826). `dismissed` is this page load's memory of the
				// tap (the durable snooze ladder lives on me.account, written by the API); `rendered` is
				// whether the component actually drew anything, so layoutStyles only reserves height
				// for a real bar.
				mobileAppBannerDismissed: false,
				mobileAppBannerRendered: false,
				authChannel: null,
				appBar: params.hideAppBar == "true" || embed ? false : true,
				navigation: params.hideNavigation == "true" || embed ? false : true,
				embed: embed,
				constants: constants,
				isAuthenticated: false,
				blocked: false,
				// LSP-15861: "position" stays "top center". The app-bar overlap on Home, Settings, and
				// Apps came from the counter-v-application-top class (margin-top: -60px !important),
				// which cancelled Vuetify's own `padding: var(--v-layout-top)` on .v-snackbar. Dropping
				// that class is the whole fix — a top-located snackbar clears the app bar unaided. R94
				// also moved this to "bottom center", which was unnecessary; don't move it back.
				//
				// "kind" distinguishes the not-connected install prompt (permanently dismissible, gets
				// the "Don't show again" button) from the reused success/failure connect toasts
				// (kind: "toast", session-only, no permanent-dismiss affordance) — see connectAccount() and
				// possiblyShowPluginInstallations().
				pluginInstallation: {
					kind: "install-prompt",
					color: "warning",
					icon: "icon-warning-triangle",
					mode: "multi-line",
					position: "top center",
					timeout: -1,
					title: "Almost there!",
					text: "",
					href: "",
					visible: false,
					showAction: true
				},
				// LSP-15861: session-scoped only (never persisted) — lets the install prompt
				// show once on the Integrations/Apps page even after the user has
				// permanently dismissed it elsewhere (AC5). Resets on every fresh app load.
				// Per-ACCOUNT (i.e. per-org) set of accounts whose once-per-session Integrations reminder
				// has already been shown. A single app-wide boolean would suppress the reminder in every
				// other org for the rest of the session, even though banner state is per-org on Account.
				pluginBannerReminderShownAccountIds: [],
				me: null,
				account: null,
				profileShown: false,
				showSwitchOrganizationDialog: false,
				maintenanceInformation: {
					color: "primary",
					position: "top",
					timeout: -1,
					visible: false,
					title: "",
					text: "",
					mode: "multi-line"
				},
				isTrial: false,
				isTrialCCNow: false,
				isActiveTrial: false,
				isEndedTrial: false,
				trialDaysRemaining: 10,
				isSubscriptionActive: true,
				isSubscriptionOwner: false,
				deferredPrompt: null,
				allowProjectPage: false,
				allowSettingsPage: false,
				projectId: "",
				shareId: "",
				notifications: [],
				showNotification: false,
				unreadNotificationCount: 0,
				totalNotifications: 0,
				getNotificationTitle,
				getNotificationIcon,
				getNotificationTime,
				subscription: null,
				isSmallPhone: DeviceUtility.isSmallPhone,
				forceRerenderKey: false,
				helpMenuOpen: false,
				showFeedbackDialog: false,
				showReferralDialog: false,
				showSupportDialog: false,
				showExchangeAuthHealthBar: false,
				// LSP-22199: why Appcues is off for the current org (null = on, or not applicable); set by storeUserAccount().
				appcuesSkipReason: null,
				// Bumped each time a skipped org clears the Appcues identity, so an older in-flight call can tell.
				appcuesClearCount: 0,
				// True while the newest storeUserAccount() call is an Appcues-skipped org on a host that loads Appcues.
				appcuesLatestCallSkipped: false,
				showExchangeAuthHealthDialog: false,
				authHealthFailingPlatforms: [],
				shellBannerHeight: null,
				shellAppBarHeight: null,
				searchAppBarHeight: 64,
				shellSessionLostBannerHeight: null,
				_shellLayoutResizeObserver: null,
				_shellObservedBannersElement: null,
				_shellObservedAppBarElement: null,
				_shellObservedSearchElement: null,
				_shellObservedSessionLostBannerElement: null,
				_shellLayoutMissingRefWarningKey: "",
				isNewSubscription: false,
				isKeyboardVisibleInLandscape: false,
				landscapeBaselineHeight: null,
				_bootAccountOnlineRetryHandler: null,
				_bootAccountOnlineRetryCleanup: null,
				_bootAccountOnlineRetryCancelled: false,
				_bootAccountSessionRecoveryStop: null,
				_uploadResumeInterval: null,
				_uploadResumeWarmupTimers: null,
				_uploadResumeRecoveryTimer: null,
				_uploadResumeRecoveryAttempt: 0,
				_lastAnnouncedUploadRetrySessionIds: null,
				_lastAnnouncedUploadFailedSessionIds: null,
				_onlineResumeHandler: null,
				_uploadDeferredResumeHandler: null,
				_uploadResumePollingStarting: false,
				_uploadDrain: null
			};
		},
		computed: {
			// LSP-15861: the plugin-install banner's two action gates, named once so the template can
			// ask "will ANY action render?" without restating either condition a third time.
			// See the "Don't show again" button for why it is limited to the first-time prompt.
			showPluginInstallDismissForever() {
				return this.pluginInstallation.kind === "install-prompt" && !this.me?.account?.pluginInstallBannerState;
			},
			showPluginInstallClose() {
				return this.pluginInstallation.timeout === -1;
			},
			// The actions row must not render when it would be empty: the outer container's `ga-2`
			// applies a gap between every direct child, so an empty second child costs the reused
			// connect success/failure toast (kind "toast", timeout 3500 — neither action renders)
			// dead vertical space on xs and dead horizontal space from sm up.
			showPluginInstallActions() {
				return this.showPluginInstallDismissForever || this.showPluginInstallClose;
			},
			displayName() {
				if (this.me && this.me.user) {
					if (this.me.user.firstname) {
						return `${this.me.user.firstname}${this.me.user.lastname ? " " + this.me.user.lastname : ""}`;
					}
					return this.me.user.email;
				}
				return "-";
			},
			userInitials() {
				let name = [];
				if (this.me && this.me.user && this.me.user.firstname) {
					name.push(this.me.user.firstname.substring(0, 1));
				}
				if (this.me && this.me.user && this.me.user.lastname) {
					name.push(this.me.user.lastname.substring(0, 1));
				}
				if (this.me && this.me.user && !this.me.user.firstname && !this.me.user.lastname) {
					name.push(this.me.user.email.split("@")[0].substring(0, 2));
				}
				return name.join("");
			},
			isSheetView() {
				return this.$route.name === "sheet-view";
			},
			// LSP-17770/17773: the /share/:token claim landing is a PUBLIC page a signed-out
			// recipient lands on — like sheet-view, it must render its router-view (and hide
			// the authenticated shell) instead of the infinite LoadingPage.
			isShareClaimView() {
				return this.$route.name === "share-claim";
			},
			isMobileUploadsView() {
				return this.$route.name === "mobile-uploads";
			},
			isPublicUnauthedView() {
				return (this.isSheetView || this.isShareClaimView || this.isMobileUploadsView) && !this.isAuthenticated;
			},
			areButtonsSticky() {
				return (
					this.$route.name === "conversation" ||
					this.$route.name === "contact" ||
					this.$route.name === "organization" ||
					this.$route.name === "user"
				);
			},
			// The pages that offer the app banner, and the Datadog surface each one reports (LSP-21826).
			mobileAppBannerSurface() {
				return mobileAppBannerSurfaceForRoute(this.$route?.name);
			},
			showMobileAppBanner() {
				// Signed-in employees only, on the dashboard home page (the quick-actions view) and the
				// Start a Recording / Take Photos / Upload Photos pages, in the full app (not embedded), and only while the API's global
				// MOBILE_APP_BANNER_ENABLED switch is on (/me config.featureFlags.mobile_app_banner).
				// Browser-level eligibility (phone or tablet, not already the Home Screen web app) and the
				// account's snooze / opt-out state are decided inside MobileAppBanner from me.account
				// (LSP-21826).
				return (
					isMeFeatureFlagEnabled(this.me, "mobile_app_banner") &&
					this.isAuthenticated &&
					!!this.me?.account &&
					!this.embed &&
					!this.isPublicUnauthedView &&
					!!this.mobileAppBannerSurface &&
					!this.mobileAppBannerDismissed
				);
			},
			showTrialBar() {
				return (
					(this.subscription &&
						(this.subscription.accountStatus === "trial" ||
							this.subscription.accountStatus === "trialExpired" ||
							this.subscription.isInPendingPaymentGracePeriod)) ||
					(this.isActiveTrial && !this.isSubscriptionActive) ||
					(this.isEndedTrial && !this.isSubscriptionActive)
				);
			},
			noPaddingRequired() {
				const usesEmbeddedSparkEditor =
					window.env.VITE_ENABLE_EMBEDDED_SPARK_EDITOR === "true" &&
					Boolean(window.env.VITE_SCRIBE_ADMIN_URL?.trim());
				return (
					this.$route.name === "conversation" ||
					// The embedded Spark editor owns its own header and scroll area.
					// Letting the generic app-container padding through leaves a gap
					// between the fixed shell divider and its toolbar, and that gap
					// moves as the outer page scrolls.
					(usesEmbeddedSparkEditor && this.$route.name === "template-new") ||
					(!this.display.mobile.value && this.$route.name === "project") ||
					this.$route.name === "search"
				);
			},
			isSettingsAdmin() {
				const roleNames = this.me?.account?.roles?.map((role) => role.name) ?? [];
				return roleNames.some((name) => isRoleOrInRoles(name, [Role.Administrator, Role.SuperAdministrator, Role.Support]));
			},
			showWelcomeWizard() {
				const org = this.me?.organization;
				if (!org || !org.welcomeWizardEnabled) {
					return false;
				}
				if (Array.isArray(org.Industries) && org.Industries.length > 0 && org.timezone != null) {
					return false;
				}
				// Scan every role (not just roles[0]) so a multi-role Administrator is recognized,
				// matching isSettingsAdmin / the route guard (LSP-16036).
				return (this.me?.account?.roles ?? []).some((role) => isRoleOrInRoles(role?.name, [Role.Administrator]));
			},
			navigationItems() {
				return [
					{
						id: "home",
						text: "Home",
						icon: "icon-home-solid",
						mobileIcon: "icon-home-stroke",
						slots: ["left", "bottom"],
						showInBottom: true,
						to: "/",
						permission: ""
					},
					{
						id: "conversations",
						text: "My Stuff / Sessions",
						// LSP-16424: the visible label was renamed "My Stuff" → "My Stuff / Sessions", but the
						// v-test / v-track selectors are derived from the label text. Pin them to the original
						// "My Stuff" via testLabel so automation selectors and analytics events stay stable
						// (the ticket requires the test tags to be left unchanged).
						testLabel: "My Stuff",
						icon: "icon-conversation-solid",
						mobileIcon: "icon-conversation-stroke",
						slots: ["left", "bottom"],
						showInBottom: true,
						to: "/conversations",
						permission: "CONVERSATION"
					},
					{
						id: "projects",
						text: "Projects",
						icon: "icon-form",
						mobileIcon: "icon-form",
						slots: ["left", "bottom"],
						showInBottom: true,
						to: "/projects",
						permission: "PROJECT"
					},
					{
						id: "reports",
						text: "AI Reports",
						icon: "mdi-file-chart-outline",
						iconSize: "1.5rem",
						slots: ["left"],
						to: "/reports",
						permission: "",
						shouldShow: () => isReportsEnabled()
					},
					{
						id: "templates",
						text: "Sparks - AI",
						// LSP-16990: the visible label was renamed "Sparks" → "Sparks - AI", but the
						// v-test / v-track selectors are derived from the label text. Pin them to the
						// original "Sparks" via testLabel so automation selectors and analytics events
						// stay stable (matches the "My Stuff" pattern above).
						testLabel: "Sparks",
						icon: "mdi-creation",
						iconSize: "1.5rem",
						slots: ["left"],
						to: "/sparks",
						mobileExcluded: true,
						permission: "",
						locked: () => hasFreeTrialEnded(this.me?.organization, this.me?.organizationSubscription),
						shouldShow: () => this.isSettingsAdmin
					},
					{
						// LSP-21829: immediately after Sparks - AI. Every org user can read reviews; the API route
						// requires READ_CONVERSATION, so the item uses the same permission rather than showing a
						// page that could only fail to load.
						id: "reviews",
						text: "Reviews",
						icon: "mdi-star-outline",
						iconSize: "1.5rem",
						slots: ["left"],
						to: "/reviews",
						permission: "CONVERSATION",
						// The API's CUSTOMER_REVIEW_PAGE_ENABLED switch, via /me config.featureFlags.customer_review_page.
						shouldShow: () => isMeFeatureFlagEnabled(this.me, "customer_review_page")
					},
					{
						// Moved below Sparks - AI so AI Reports and Sparks - AI sit next to each other in the nav.
						id: "contacts",
						text: "Contacts",
						icon: "icon-contacts-stroke",
						mobileIcon: "icon-contacts-stroke",
						slots: ["left", "bottom"],
						showInBottom: true,
						to: "/contacts",
						permission: "CUSTOMER"
					},
					{
						id: "forms",
						text: "Web Forms",
						icon: "icon-globe-website-stroke",
						slots: ["left"],
						permission: "ORGANIZATION",
						to: "/forms",
						mobileExcluded: true,
						locked: () => hasFreeTrialEnded(this.me?.organization, this.me?.organizationSubscription),
						shouldShow: () => this.isSettingsAdmin
					},
					{
						id: "integrations",
						text: "Integrations",
						icon: "icon-puzzle-piece-solid",
						slots: ["left"],
						permission: "ORGANIZATION",
						to: "/integrations",
						mobileExcluded: true,
						locked: () => hasFreeTrialEnded(this.me?.organization, this.me?.organizationSubscription),
						shouldShow: () => this.isSettingsAdmin,
						// LSP-15861 (AC4): a small "you dismissed a reminder" badge — shown only
						// once the user has dismissed the plugin-install banner AND an unconnected
						// installation still exists. Clears when acknowledgePluginInstallBannerIfDismissed
						// (fired by the Integrations route watcher) flips state to "acknowledged".
						// navigationItems() is itself a computed, so reading this.me here keeps the
						// badge reactive to me.account.pluginInstallBannerState changes. State lives on
						// the current-org Account (not the identity-global user), so switching orgs no
						// longer wipes another org's dismissal.
						bannerText:
							this.me?.account?.pluginInstallBannerState === "dismissed" && this.findUnconnectedPluginInstallation()
								? "1"
								: undefined
					},
					{
						id: "settings",
						text: "Settings",
						icon: "icon-cog-gear",
						slots: ["left"],
						permission: "ORGANIZATION",
						to: () => this.organizationUrl,
						locked: () => hasFreeTrialEnded(this.me?.organization, this.me?.organizationSubscription),
						disabled: () => !this.allowSettingsPage,
						disabledTooltip: this.constants.PREMIUM_MESSAGE_ADMIN,
						shouldShow: () => this.isSettingsAdmin
					},
					{
						id: "coaching-videos",
						text: "Coaching Videos",
						icon: "icon-video-play-stroke",
						slots: ["left"],
						to: "/videos",
						permission: "",
						mobileExcluded: true
					},
					{
						id: "users",
						text: "Users",
						icon: "icon-users-3",
						slots: ["profile"],
						permission: "USER",
						to: "/users",
						shouldShow: () => this.isSettingsAdmin
					},
					{
						// LSP-15603: profile-menu entry restoring the top-level org switcher so users
						// without Settings permission can still switch organizations. permission is left
						// empty so isItemVisible() does not short-circuit on READ_ORGANIZATION — the
						// User role lacks it. shouldShow() handles the only real gate: more than one org.
						id: "switch-organization",
						text: "Switch Organization",
						icon: "icon-building-exit",
						slots: ["profile"],
						permission: "",
						to: "",
						onClick: () => {
							this.showSwitchOrganizationDialog = true;
						},
						shouldShow: () => (this.me?.organizations?.length ?? 0) > 1
					},
					{
						id: "billing",
						text: "Billing",
						icon: "icon-credit-card",
						slots: ["profile"],
						permission: "ORGANIZATION",
						to: "",
						onClick: () => this.navigateToSubscription(),
						shouldShow: () => {
							if (window.env.VITE_ENABLE_PURCHASE_MODULE == "false") {
								return false;
							}
							if (!this.isNewSubscription) {
								return false;
							}
							if (this.me?.account.roles[0].name.toLowerCase() === "user") {
								return false;
							}

							const isTrialState =
								this.subscription?.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL ||
								this.subscription?.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL_EXPIRED;

							if (isTrialState) {
								return false;
							}

							return this.me?.organization.isTrial || this.subscription;
						}
					}
				];
			},
			leftItems() {
				return this.navigationItems.filter((item) => item.slots.includes("left") && this.isItemVisible(item));
			},
			navigationReady() {
				return Boolean(this.account && !this.loading && !this.bootErrorMessage);
			},
			bottomItems() {
				return this.navigationItems.filter(
					(item) => item.slots.includes("bottom") && item.showInBottom && this.isItemVisible(item)
				);
			},
			profileItems() {
				return this.getProfileMenuItems().sort((firstItem, secondItem) => {
					const firstOrder = this.getProfileItemOrder(firstItem.id);
					const secondOrder = this.getProfileItemOrder(secondItem.id);
					return firstOrder - secondOrder;
				});
			},
			organizationName() {
				// Token first (updates on silent rotation); /users/me name as a boot-time fallback.
				return this.authSession?.organizationName || this.me?.organization?.name || "";
			},
			sessionLostBannerVisible() {
				// Show only when authSession says we're lost AND the user
				// hasn't dismissed this iteration. The watcher below resets
				// the dismissal flag whenever sessionLost transitions false →
				// true, so a new failure re-shows the banner.
				return Boolean(this.authSession?.sessionLost && !this.sessionLostBannerDismissed);
			},
			organizationUrl() {
				return "/settings";
			},
			currentOrganizationId() {
				// Token-derived id reflects silent rotations; cached me.organization.id is the
				// boot fallback before authSession decodes. Single source of truth for the
				// active org — feeds the SwitchOrganizationDialog "Current" chip and the
				// subscription URL below.
				return this.authSession?.organizationId || this.me?.organization?.id || undefined;
			},
			subscriptionUrl() {
				return this.currentOrganizationId ? `/subscriptions/${this.currentOrganizationId}` : "";
			},
			mini() {
				return this.display.smAndDown.value;
			},
			layoutStyles() {
				const shellBannerCount =
					Number(this.showTrialBar) +
					Number(this.showExchangeAuthHealthBar) +
					Number(Boolean(this.showMobileAppBanner && this.mobileAppBannerRendered));
				// Keep the first render stable before ResizeObserver has measured the actual bars. Once
				// mounted, the measured values handle wrapping, responsive changes, and conditional bars.
				const shellBannerHeight = this.shellBannerHeight ?? shellBannerCount * 52;
				const appTopBarHeight = this.shellAppBarHeight ?? (this.appBar && !this.isPublicUnauthedView ? 64 : 0);
				const shellChromeHeight = shellBannerHeight + appTopBarHeight;
				const sessionLostBannerHeight = this.shellSessionLostBannerHeight ?? 0;

				return {
					"--shell-banner-height": `${shellBannerHeight}px`,
					"--shell-chrome-height": `${shellChromeHeight}px`,
					"--shell-header-height": `${shellChromeHeight + sessionLostBannerHeight}px`
				};
			},
			menuMinWidth() {
				return this.display.mobile.value ? "280" : "360";
			},
			notificationMenuMaxWidth() {
				return this.display.mobile.value ? "calc(100vw - 16px)" : "360";
			},
			currentActiveUrl() {
				const activeItem = this.bottomItems.find((item) => this.isNavItemActive(item));
				return activeItem ? this.resolveItemTo(activeItem) : undefined;
			}
		},
		created() {
			EventBus.$on("NotificationRead", (notification) => {
				const notificationId = notification.id;
				const notificationIndex = this.notifications.findIndex((n) => n.id === notificationId);
				if (notificationIndex !== -1 && !this.notifications[notificationIndex].isRead) {
					this.notifications[notificationIndex].isRead = true;
					this.unreadNotificationCount--;
				}
			});
			EventBus.$on("userProfileUpdated", async () => {
				try {
					this.me = await api.refreshMe();
					await this.storeUserAccount();
				} catch (e) {
					console.error("Failed to refresh current user data after profile update", e);
				}
			});
		},
		watch: {
			"authSession.sessionLost"(newVal, oldVal) {
				// Reset dismissal whenever sessionLost transitions false → true
				// so a fresh session-loss event re-shows the banner the user
				// previously dismissed. If the dismissed flag persisted, a user
				// who clicked X once would never see the warning again — even
				// after recovering their session and losing it a second time.
				if (newVal && !oldVal) {
					this.sessionLostBannerDismissed = false;
				}
			},
			// LSP-15861 (AC4/AC5): landing on Integrations (a) may still show the install
			// prompt once per session even after a permanent dismissal, and (b) is the
			// trigger that acknowledges a "dismissed" state so the sidebar badge clears.
			"$route.name"(newRouteName) {
				if (newRouteName !== "integrations") {
					return;
				}
				// Bugbot (PR #2980, M4): each call gets its OWN try/catch, mirroring the org-switch
				// call site in refreshCurrentUserContextForOrgSwitch — without this, a throw from
				// possiblyShowPluginInstallations would skip the acknowledge call entirely and leave
				// the sidebar badge stuck.
				try {
					this.possiblyShowPluginInstallations();
				} catch (error) {
					logError("App: possiblyShowPluginInstallations failed on route change to integrations", error);
				}
				try {
					this.acknowledgePluginInstallBannerIfDismissed();
				} catch (error) {
					logError("App: acknowledgePluginInstallBannerIfDismissed failed on route change to integrations", error);
				}
			}
		},
		async beforeUnmount() {
			this._shellLayoutResizeObserver?.disconnect();
			this._shellLayoutResizeObserver = null;
			this._shellObservedBannersElement = null;
			this._shellObservedAppBarElement = null;
			this._shellObservedSessionLostBannerElement = null;
			this._shellLayoutMissingRefWarningKey = "";
			if (this._organizationSyncCleanup) {
				this._organizationSyncCleanup();
			}
			if (this._authSessionStop) {
				this._authSessionStop();
				this._authSessionStop = null;
			}
			// LSP-19055: the deferred switch verdict is scoped to this document. Dropping it here keeps
			// a torn-down instance from being the thing that decides (it never can — the watcher above
			// is gone — but leaving the field set would misrepresent the state in a devtools/HMR poke).
			this._pendingOrgSwitchAwaitingOrganizationId = null;
			this._pendingOrgSwitchAwaitingId = null;
			// Abort any in-flight org-switch refresh so its setters can't
			// run after the component unmounts (typically only matters in
			// tests / HMR).
			this.orgSwitchRequestScope.abortCurrent();
			this._bootAccountOnlineRetryCancelled = true;
			if (this._bootAccountOnlineRetryCleanup) {
				this._bootAccountOnlineRetryCleanup();
			} else if (this._bootAccountOnlineRetryHandler) {
				window.removeEventListener("online", this._bootAccountOnlineRetryHandler);
				this._bootAccountOnlineRetryHandler = null;
			}
			// LSP-19862: the session-lost recovery watcher lives as long as the retry ladder does.
			this.disarmBootAccountSessionRecovery();
			this.authSession.dispose();
			this.stopUploadResumePolling();
			resetAdminSmallFileUploadLaneForTests();
			EventBus.$off("userProfileUpdated");
			if (window.visualViewport) {
				window.visualViewport.removeEventListener("resize", this.handleVisualViewportResize);
			}
			if (window.screen.orientation) {
				window.screen.orientation.removeEventListener("change", this.updateLandscapeBaseline);
			}
			window.removeEventListener("orientationchange", this.updateLandscapeBaseline);
			window.removeEventListener("message", this.handleAuthMessage);
			if (this.authChannel) {
				this.authChannel.close();
				this.authChannel = null;
			}
			Socket.off("update", this.getNotifications);
			Socket.off("delete", this.getNotifications);
			Socket.off("mention", this.getNotifications);
			Socket.off("comment", this.getNotifications);
			api.setNoAccountDialogHandler(null);
		},
		async errorCaptured(err, vm, info) {
			// LSP-20408: this hook sits on the ROOT component, so it catches every uncaught error from
			// every descendant — a click handler, a watcher, a lifecycle hook, a rejected promise Vue
			// knows about. It used to toast `err.message` verbatim, which put internal developer text
			// (type errors, bundle URLs, ids) in a red banner in front of the customer, for failures
			// they can do nothing about. Classify instead of silencing: the customer reads one generic
			// sentence, and the full error still goes to the console and to Datadog via logError.
			logError(`App.errorCaptured: unhandled error from ${info}`, err, { info });
			if (err && err.message) {
				// One sentence for every source. Vue only spells `info` out ("render function") in a dev
				// build — a production bundle passes "https://vuejs.org/error-reference/#runtime-<code>"
				// instead — so branching the customer copy on it would be dead code in production. `info`
				// is still the classification input, in the log above, where the raw string is useful.
				const message = genericFailure();
				// errorCaptured can fire before setup() resolves on early child errors;
				// guard so the error handler itself does not throw.
				this.toast?.error(message, { timeout: calculateTimeout(message) });
			}
		},
		async mounted() {
			this.syncShellLayoutObserver();
			if (window.visualViewport) {
				window.visualViewport.addEventListener("resize", this.handleVisualViewportResize);
			}
			if (window.screen.orientation) {
				window.screen.orientation.addEventListener("change", this.updateLandscapeBaseline);
			}
			window.addEventListener("orientationchange", this.updateLandscapeBaseline);
			// Handle authentication requests via BroadcastChannel (for cross-subdomain communication)
			if (window.BroadcastChannel) {
				try {
					this.authChannel = new BroadcastChannel("liveswitch-contact-auth");
					this.authChannel.addEventListener("message", this.handleBroadcastAuthMessage);
					console.log("[contact-admin] BroadcastChannel auth listener established");
				} catch (error) {
					console.error("[contact-admin] Failed to setup BroadcastChannel:", error);
				}
			} else {
				console.log("[contact-admin] BroadcastChannel not supported");
			}
			this.updateLandscapeBaseline();
			// LSP-18866: register the no-account dialog handler BEFORE the first await in mounted().
			// api.openNoAccountDialog is fire-and-forget and no-ops when no handler is registered,
			// and the router's authenticated-boot guard can reach its terminal org-switch branch
			// while these awaits are still pending — that dialog is the only user-visible signal on
			// that path, so losing it to a startup race turns a handled failure back into a silent
			// one. Registering here needs nothing but $refs, which exist by mounted(). It is
			// re-registered below alongside the other $root dialog wiring; setNoAccountDialogHandler
			// simply overwrites, so the duplicate is harmless and the early one is what closes the
			// race.
			api.setNoAccountDialogHandler((reason, message) => {
				this.$refs.noAccount.open(reason, message);
			});
			// The mobile recovery handoff is deliberately public and has no account-backed behavior.
			// Avoid starting authenticated boot work that would turn its safe browser fallback into
			// a failing API flow for signed-out email recipients.
			if (window.location.pathname === "/mobile/uploads") {
				this.loading = false;
				return;
			}
			// Must run before useOrgRouteGuard so the guard sees a hydrated org id.
			await this.authSession.initialize();
			const { cleanup } = useOrgRouteGuard();
			this._organizationSyncCleanup = cleanup;
			this._authSessionStop = this.$watch(
				() => this.authSession.organizationId,
				async (newOrgId, oldOrgId) => {
					// LSP-19055: deliberately ABOVE the guard below, which returns early when there was no
					// previous org id — and "no previous org id" is exactly the unreadable-at-boot case a
					// deferred switch verdict is waiting on. Reuses this watcher rather than adding a
					// second one so the existing _authSessionStop teardown covers it too.
					this.readjudicatePendingOrgSwitchIfArmed(newOrgId);
					if (!oldOrgId || !newOrgId || newOrgId === oldOrgId) {
						return;
					}
					await this.refreshCurrentUserContextForOrgSwitch();
					// Remount the active route so per-page mounted() refetches under the new
					// org. List pages (Home, Projects, My Stuff) load data once in mounted()
					// and aren't covered by useOrgRouteGuard's redirect, so without this
					// they keep showing the previous org's data.
					this.forceRerenderKey = !this.forceRerenderKey;
				}
			);
			// LSP-19055: AFTER the watcher above is registered, and still BEFORE the network-dependent boot
			// chain below — so a slow or failing getMe cannot swallow the notice, and nothing can slip
			// through the gap either. Vue's $watch samples its source when it is registered and does NOT
			// invoke the callback for that first value, so adjudicating BEFORE registration meant a token
			// event landing in between (authSession refreshes on storage, BroadcastChannel and
			// onTokenChanged) became the watcher's silent baseline: the verdict stayed armed for a change
			// that had already happened, and the user got nothing (break-it round 2). Registering first
			// closes that window — there is no await between the registration and this call, so the
			// organization can only become readable before this reads it (adjudicated here) or after
			// (adjudicated by the live watcher).
			this.surfacePendingOrgSwitchFailure();
			try {
				this.isAuthenticated = await this.$auth.isAuthenticated();
				this.blocked = this.$auth.isBlocked();
			} catch (e) {
				console.warn("Error checking auth state", e);
			}
			Socket.on("update", this.getNotifications);
			Socket.on("delete", this.getNotifications);
			Socket.on("mention", this.getNotifications);
			Socket.on("comment", this.getNotifications);
			Socket.on("connect", () => {});

			try {
				this.$root.$confirm = this.$refs.confirm;
				this.$root.$noAccount = this.$refs.noAccount;
				this.$root.$notification = this.$refs.notification;
				this.$root.$freeTrialEndedDialog = this.$refs.freeTrialEndedDialog;
				this.$root.$fileUploadDialog = this.$refs.fileUploadDialog;
				this.$root.$createCustomerDialog = this.$refs.createCustomerDialog;
				this.$root.$checkExchangeAuthHealth = this.checkExchangeAuthHealth;
				api.setNoAccountDialogHandler((reason, message) => {
					this.$refs.noAccount.open(reason, message);
				});
				this.loading = true;
				this.maintenanceInformation.color = window.env.VITE_MAINTENANCE_MODE_COLOR;
				this.maintenanceInformation.title = window.env.VITE_MAINTENANCE_MODE_TITLE;
				this.maintenanceInformation.text = window.env.VITE_MAINTENANCE_MODE_TEXT;
				this.maintenanceInformation.visible = window.env.VITE_MAINTENANCE_MODE_ACTIVE === "true";
				if (this.isPublicUnauthedView) {
					this.loading = false;
					return;
				}
				// Start upload recovery BEFORE the network-dependent init chain below. On an iOS-Safari
				// reopen right after a tab kill the network is briefly flaky (SSE "Load failed"), so an
				// awaited getMe/ensureLoaded/getNotifications can hang and would otherwise block recovery
				// entirely (the exact silent-loss window this fixes). Recovery only needs the auth token
				// (already set) and local IndexedDB, not this.me — so run it independently, up front.
				this.startUploadResumePolling();
				try {
					await this.loadBootAccountForStartup();
				} finally {
					this.loading = false;
				}
			} catch (error) {
				console.warn("Error initializing app", JSON.stringify(error));
				// if user doesnt exist, we don't want to log it
			}
		},
		updated() {
			this.syncShellLayoutObserver();
		},
		methods: {
			// LSP-19055: the user-facing half of the persisted switch-failure notice. auth.js records
			// the requested organization before handing navigation to Auth0; this reads that marker
			// back on the next boot and tells the user if they are not where they asked to be.
			// Everything about WHICH outcomes deserve a message (including the callback-document
			// deferral and the unreadable-organization case) lives in consumePendingOrgSwitch — this
			// method only renders the one outcome that is the user's business.
			surfacePendingOrgSwitchFailure() {
				try {
					const pendingOrgSwitch = consumePendingOrgSwitch(this.authSession.organizationId);
					if (pendingOrgSwitch.outcome === "failed") {
						this.warnPendingOrgSwitchFailed();
						return;
					}
					if (pendingOrgSwitch.outcome === "unknown") {
						// The organization was not readable yet — usually a token read that failed at boot
						// and succeeds on the next refresh (authSession refreshes on storage events,
						// BroadcastChannel and onTokenChanged). The marker is already consumed, so hold the
						// requested organization in memory and let the authSession watcher below decide once
						// the organization arrives. Without this, a boot-time network blip on top of a
						// failed switch reproduces the exact silence LSP-19055 is about.
						this._pendingOrgSwitchAwaitingOrganizationId = pendingOrgSwitch.organizationId;
						// The notice's exact identity, so the resolution below can prove the marker it spends is
						// still the same one this verdict was armed for (break-it round 7).
						this._pendingOrgSwitchAwaitingId = pendingOrgSwitch.pendingOrgSwitchId;
					}
				} catch (error) {
					// Never let the notice break boot: everything after this call in mounted() is the
					// actual application startup.
					logError("App: failed to surface a pending organization-switch failure", error);
				}
			},
			// Second half of the unreadable-organization case above, driven by the authSession
			// organizationId watcher. In-memory only and one-shot: it is armed at most once per document
			// and disarmed on the first readable organization, whatever the verdict, so it can neither
			// re-warn nor outlive this document.
			readjudicatePendingOrgSwitchIfArmed(organizationId) {
				const requestedOrganizationId = this._pendingOrgSwitchAwaitingOrganizationId;
				// normalizeOrganizationId, not a bare truthiness check: a whitespace-only claim is
				// UNREADABLE, not "some other organization", so it must leave the verdict armed rather
				// than resolve it as a failure (break-it round 1).
				const currentOrganizationId = normalizeOrganizationId(organizationId);
				if (!requestedOrganizationId || !currentOrganizationId) {
					return;
				}
				// The durable marker is the source of truth, and this armed verdict is only a timeliness aid
				// on top of it — so the verdict may act ONLY while the marker is still the one it was armed
				// for. Two ways that stops being true, both reachable after an unreadable boot arms a
				// verdict for org A:
				//   - a newer EXPLICIT switch to B SUCCEEDS, which retires the marker in auth.js. Acting
				//     here would compare B against the superseded request A and tell a user who just
				//     switched successfully that we could not switch them (break-it round 4, AC2).
				//   - a newer explicit switch to B is handed to its own redirect, which OVERWRITES the
				//     marker with B. Acting here would spend B's notice on A's behalf, so B's failure goes
				//     unreported (break-it round 6, AC1).
				// Comparing owners rather than merely asking whether a marker exists covers both.
				const armedPendingOrgSwitchId = this._pendingOrgSwitchAwaitingId;
				const pendingOrgSwitchId = readPendingOrgSwitchId();
				this._pendingOrgSwitchAwaitingOrganizationId = null;
				this._pendingOrgSwitchAwaitingId = null;
				if (!armedPendingOrgSwitchId || pendingOrgSwitchId !== armedPendingOrgSwitchId) {
					logInfo("App: discarded a deferred organization-switch verdict that no longer owns the pending notice", {
						requestedOrganizationId,
						currentOrganizationId,
						armedPendingOrgSwitchId,
						pendingOrgSwitchId
					});
					return;
				}
				// The durable marker survived the unreadable boot on purpose (so a document that navigates
				// away does not lose the notice), which makes THIS the point at which it is spent. Keyed,
				// for the same reason the auth-side abandon is.
				clearPendingOrgSwitchFor(armedPendingOrgSwitchId);
				if (currentOrganizationId === requestedOrganizationId) {
					logWarn("App: a pending organization switch resolved as successful once the organization became readable", {
						requestedOrganizationId
					});
					return;
				}
				logWarn("App: a pending organization switch resolved as failed once the organization became readable", {
					requestedOrganizationId,
					currentOrganizationId
				});
				// Disarmed ABOVE this call, and wrapped: this runs from the authSession watcher, which
				// has no catch of its own, so a throwing toast plugin would surface as an unhandled
				// rejection in the watcher rather than as a missing notice (break-it round 1). The boot
				// path has the same protection in surfacePendingOrgSwitchFailure.
				try {
					this.warnPendingOrgSwitchFailed();
				} catch (error) {
					logError("App: failed to surface a deferred organization-switch failure", error);
				}
			},
			// One home for the copy so the boot verdict and the deferred verdict cannot drift apart.
			warnPendingOrgSwitchFailed() {
				// A warning, not an error: the app is in a consistent, usable state — the user is
				// genuinely in their previous organization and the top bar says so. What went wrong is
				// the switch, and retrying is the useful next step.
				const message = "We couldn't switch organizations, so you're still in your previous one. Please try again.";
				this.toast.warning(message, { timeout: calculateTimeout(message) });
			},
			syncShellLayoutObserver() {
				const resolveElement = (reference) => {
					const element = reference?.$el ?? reference;
					return element instanceof HTMLElement ? element : null;
				};
				const bannersElement = resolveElement(this.$refs.shellBanners);
				const appBarElement = resolveElement(this.$refs.appTopBar);
				const searchElement = resolveElement(this.$refs.globalSearch);
				const sessionLostBannerElement = resolveElement(this.$refs.sessionLostBanner);
				const missingExpectedRefs = [
					this.showTrialBar || this.showExchangeAuthHealthBar || this.showMobileAppBanner ? !bannersElement && "shellBanners" : false,
					this.appBar && !this.isPublicUnauthedView ? !appBarElement && "appTopBar" : false,
					this.sessionLostBannerVisible ? !sessionLostBannerElement && "sessionLostBanner" : false
				].filter(Boolean);
				const missingExpectedRefsKey = missingExpectedRefs.join(",");
				if (missingExpectedRefsKey && missingExpectedRefsKey !== this._shellLayoutMissingRefWarningKey) {
					logWarn("[App] Expected shell layout refs were not rendered; sticky offsets may be incomplete.", {
						missingRefs: missingExpectedRefs
					});
				}
				this._shellLayoutMissingRefWarningKey = missingExpectedRefsKey;
				const updateMeasuredHeights = () => {
					// Normal compact search is 40px in the 64px toolbar. Preserve its 12px
					// top/bottom clearance while inline details add only their occupied height.
					this.searchAppBarHeight = Math.max(64, (searchElement?.getBoundingClientRect().height ?? 0) + 24);
					this.shellBannerHeight = bannersElement?.getBoundingClientRect().height ?? 0;
					this.shellAppBarHeight = appBarElement?.getBoundingClientRect().height ?? 0;
					const sessionLostBannerMarginTop = sessionLostBannerElement
						? Number.parseFloat(getComputedStyle(sessionLostBannerElement).marginTop) || 0
						: 0;
					this.shellSessionLostBannerHeight =
						(sessionLostBannerElement?.getBoundingClientRect().height ?? 0) + sessionLostBannerMarginTop;
				};

				if (
					bannersElement === this._shellObservedBannersElement &&
					appBarElement === this._shellObservedAppBarElement &&
					searchElement === this._shellObservedSearchElement &&
					sessionLostBannerElement === this._shellObservedSessionLostBannerElement
				) {
					if (typeof ResizeObserver === "undefined") {
						updateMeasuredHeights();
					}
					return;
				}

				this._shellLayoutResizeObserver?.disconnect();
				this._shellObservedBannersElement = bannersElement;
				this._shellObservedAppBarElement = appBarElement;
				this._shellObservedSearchElement = searchElement;
				this._shellObservedSessionLostBannerElement = sessionLostBannerElement;

				updateMeasuredHeights();

				if (typeof ResizeObserver === "undefined") {
					logWarn("[App] Shell layout offsets were not initialized because ResizeObserver is unavailable.");
					this._shellLayoutResizeObserver = null;
					return;
				}

				this._shellLayoutResizeObserver = new ResizeObserver(updateMeasuredHeights);
				if (bannersElement) {
					this._shellLayoutResizeObserver.observe(bannersElement);
				}
				if (appBarElement) {
					this._shellLayoutResizeObserver.observe(appBarElement);
				}
				if (searchElement) {
					this._shellLayoutResizeObserver.observe(searchElement);
				}
				if (sessionLostBannerElement) {
					this._shellLayoutResizeObserver.observe(sessionLostBannerElement);
				}
			},
			isBootAccountLoadTransientError(error) {
				return isTransientAuthTokenError(error) || isNetworkShapedBootAccountLoadError(error);
			},
			async loadBootAccountContext() {
				this.me = await api.getMe();
				await this.rolesStore.ensureLoaded();
				await this.storeUserAccount();
				// LSP-22341: not awaited. `loading` (and with it navigationReady, the side nav and the bottom nav)
				// clears only when this method returns, and /notifications/unread-count reached a 30s p99 on
				// staging. The bell renders its own loading state; getNotifications() catches its own errors.
				this.getNotifications();
				// Each call gets its OWN try/catch, matching the $route.name watcher and the org-switch
				// path: a throw from possiblyShowPluginInstallations (e.g. malformed configuration.oauth)
				// must not abort boot before the acknowledge runs, or a "dismissed" state never becomes
				// "acknowledged" and the sidebar badge stays stuck.
				try {
					this.possiblyShowPluginInstallations();
				} catch (error) {
					logError("App: possiblyShowPluginInstallations failed during boot", error);
				}
				// LSP-15861: the "$route.name" watcher does not fire for the app's initial route,
				// so a direct URL load of /integrations would show the once-per-session prompt but
				// never persist "acknowledged" (the badge would linger). Acknowledge here too; the
				// method no-ops unless the state is exactly "dismissed".
				if (this.$route?.name === "integrations") {
					try {
						this.acknowledgePluginInstallBannerIfDismissed();
					} catch (error) {
						logError("App: acknowledgePluginInstallBannerIfDismissed failed during boot", error);
					}
				}
				this.checkExchangeAuthHealth();
				this.allowSettingsPage = !hasFreeTrialEnded(this.me.organization, this.me.organizationSubscription);
				if (this.isAuthenticated) {
					this.syncSocketAuth(this.me);
				}
				this.bootErrorMessage = "";
			},
			handleBootShareLinkAccountLoadFailure() {
				// LSP-21122: the router module rewrites `/projects/<id>/<shareId>` to the `?shareId=` form before
				// boot (LSP-20890), but skips it for a malformed percent-encoded segment and when replaceState
				// throws (sandboxed iframe). This gate runs once, so declining the path form here left the
				// signed-out recipient on LoadingPage forever. Read the path form directly — before the
				// case-sensitive project-path check below, since the parser matches case-insensitively like the
				// router. The path segment wins over a stray `?shareId=`, as in the router's record redirect.
				const pathFormShareLink = parsePathFormShareLink(window.location.pathname);
				if (pathFormShareLink) {
					// Anomalous by construction (the pre-boot rewrite should have run), so log it — without the
					// segment itself, which can be a usable share token even when it does not decode.
					logInfo("App: boot share-link gate — address bar still holds the path-form share link; mounting the public view", {
						projectId: pathFormShareLink.projectId,
						shareIdDecoded: pathFormShareLink.shareIdDecoded,
						...describeShareIdSegmentForLog(pathFormShareLink.rawShareIdSegment),
						hasStrayShareIdQuery: window.location.search.indexOf("shareId") !== -1,
						isAuthenticated: this.isAuthenticated
					});
					this.loading = false;
					this.allowProjectPage = true;
					this.projectId = pathFormShareLink.projectId;
					this.shareId = pathFormShareLink.shareId;
					return true;
				}
				const isProjectPath =
					window.location.pathname.indexOf("/projects/") !== -1 || window.location.pathname.indexOf("/projects2/") !== -1;
				if (!isProjectPath) {
					return false;
				}
				if (window.location.search.indexOf("shareId") === -1) {
					// A plain `/projects/<id>` visit: not a share link, and the router guard sends it to Auth0.
					return false;
				}

				this.loading = false;
				this.allowProjectPage = true;
				// LSP-20530: parse with the URL API so a tab hash (`#documents`) never ends up inside the share id.
				const shareLink = parseShareLinkLocation(window.location.href);
				this.projectId = shareLink.projectId;
				this.shareId = shareLink.shareId;
				return true;
			},
			retryBootAccountDeferredNavigation() {
				return retryDeferredBootAccountNavigation(this.$router);
			},
			// LSP-19862: an UnauthenticatedError is an expired-session outcome, not a boot fault, and it
			// can surface from EITHER the startup load or the transient-failure retry (token expires while
			// the tab is offline, then the reconnect drives runRetryOnce). Both phases route here so the
			// user gets the session-lost banner and its "Sign in Again" action instead of the generic
			// "refresh and try again" copy plus a Datadog error nobody can act on.
			handleBootAccountLoadUnauthenticated(error, { phase }) {
				// Stop the retry ladder: no number of retries can mint a token, so leave no armed timer or
				// `online` listener behind. Mirrors the beforeUnmount teardown.
				this._bootAccountOnlineRetryCancelled = true;
				if (this._bootAccountOnlineRetryCleanup) {
					this._bootAccountOnlineRetryCleanup();
				} else if (this._bootAccountOnlineRetryHandler) {
					window.removeEventListener("online", this._bootAccountOnlineRetryHandler);
					this._bootAccountOnlineRetryHandler = null;
				}
				// getMe raises UnauthenticatedError from its own isAuthenticated() pre-check and from a
				// null getAccessToken(), and NEITHER path emits a terminal auth event, so
				// authSession.sessionLost would stay false and the shell would render the authenticated
				// router-view with no account and no way out (Bugbot, LSP-19862). Arm the existing
				// session-lost banner (which owns the "Sign in Again" action); fall back to the boot banner
				// if the store hook is unavailable so there is always recovery UI.
				if (typeof this.authSession?.markSessionLost === "function") {
					this.authSession.markSessionLost();
					// LSP-19862 (AI review): markSessionLost() is a no-op when sessionLost is ALREADY true —
					// the terminal auth event can fire while the tab is offline, before this handler runs — so
					// the false → true watcher that normally resets the dismissal never fires. If the user had
					// dismissed the banner earlier, the shell would go interactive with no account and no
					// recovery UI at all. Re-surface it here; the watcher cannot see a true → true call.
					this.sessionLostBannerDismissed = false;
					// Drop any banner the transient phase left up — the retry path reaches here with
					// "We can't reach the service right now" already set, and a non-empty bootErrorMessage
					// swaps the shell for LoadingPage, hiding the session-lost banner we just armed.
					this.bootErrorMessage = "";
					// LSP-19862 (Bugbot): the banner is the only recovery UI, and it disappears the moment a
					// valid org token clears sessionLost — but nothing re-runs the account load, so the shell
					// would render interactive with no `me`/`account` and no way back. The org-id watcher
					// cannot cover this: it returns early for null→same-org and for a same-org rotation.
					// Arm a one-shot reload keyed on the same flag the banner reads.
					this.armBootAccountSessionRecovery();
				} else {
					this.bootErrorMessage = "Your session has expired. Please sign in again.";
				}
				logWarn("App: boot account-load hit an unauthenticated session", {
					classification: "unauthenticated",
					correlationId: `boot-account-load-${phase}`,
					phase,
					retryable: false,
					routeName: this.$route?.name ?? null,
					errorMessage: error?.message ?? null
				});
			},
			// LSP-19862: one-shot recovery for the session-lost boot outcome. Watches the same
			// authSession.sessionLost flag the banner renders from; when a later valid org token clears it
			// while the account is still missing, re-run the startup load (which succeeds, or classifies
			// the new failure exactly as before). Disposes itself before reloading so a second
			// true→false toggle cannot re-enter, and beforeUnmount tears down whatever is still armed.
			armBootAccountSessionRecovery() {
				if (this._bootAccountSessionRecoveryStop || typeof this.$watch !== "function") {
					return;
				}
				this._bootAccountSessionRecoveryStop = this.$watch(
					function () {
						return Boolean(this.authSession?.sessionLost);
					},
					async (isSessionLost) => {
						if (isSessionLost) {
							return;
						}
						// Disarm on EVERY true → false transition, including when `me` is already populated
						// (getMe succeeded and a later step of loadBootAccountContext threw
						// UnauthenticatedError). Returning early on a truthy `me` left the watcher armed
						// forever, and the _bootAccountSessionRecoveryStop guard then blocked any future arm.
						// The reload is idempotent — getMe, ensureLoaded, storeUserAccount, getNotifications —
						// so re-running it with a stale `me` is safe and refreshes the rest of the context.
						this.disarmBootAccountSessionRecovery();
						logInfo("App: boot account-load recovered after session-lost", {
							correlationId: "boot-account-load-recovery",
							routeName: this.$route?.name ?? null
						});
						await this.loadBootAccountForStartup();
					}
				);
			},
			disarmBootAccountSessionRecovery() {
				if (this._bootAccountSessionRecoveryStop) {
					this._bootAccountSessionRecoveryStop();
					this._bootAccountSessionRecoveryStop = null;
				}
			},
			scheduleBootAccountOnlineRetry() {
				if (this._bootAccountOnlineRetryHandler) {
					return;
				}

				this._bootAccountOnlineRetryCancelled = false;
				// LSP-17079: a soft flap that times out while navigator.onLine stays true never emits an
				// `online` event, so an online-only retry would leave boot stuck. Arm BOTH triggers —
				// the offline→online edge AND a timed fallback per attempt — running each retry exactly once.
				const BOOT_ACCOUNT_RETRY_DELAYS_MS = [5000, 10000, 20000, 20000, 20000];
				const BOOT_ACCOUNT_PERSISTENT_RETRY_MS = 30000;
				let retryTimerId = null;
				const maxAttempts = BOOT_ACCOUNT_RETRY_DELAYS_MS.length;
				const cleanup = () => {
					if (this._bootAccountOnlineRetryHandler) {
						window.removeEventListener("online", this._bootAccountOnlineRetryHandler);
						this._bootAccountOnlineRetryHandler = null;
					}
					if (retryTimerId !== null) {
						clearTimeout(retryTimerId);
						retryTimerId = null;
					}
					if (this._bootAccountOnlineRetryCleanup === cleanup) {
						this._bootAccountOnlineRetryCleanup = null;
					}
				};
				const scheduleAttempt = (attempt, retryDelayMs = null) => {
					if (this._bootAccountOnlineRetryCancelled || this._bootAccountOnlineRetryHandler) {
						return;
					}

					this._bootAccountOnlineRetryHandler = () => {
						void runRetryOnce(attempt);
					};
					this._bootAccountOnlineRetryCleanup = cleanup;
					window.addEventListener("online", this._bootAccountOnlineRetryHandler, { once: true });
					if (retryDelayMs !== null) {
						retryTimerId = setTimeout(this._bootAccountOnlineRetryHandler, retryDelayMs);
					}
				};
				const scheduleTimedAttempt = (attempt) => {
					scheduleAttempt(attempt, BOOT_ACCOUNT_RETRY_DELAYS_MS[attempt - 1]);
				};
				const schedulePersistentOnlineAttempt = (attempt) => {
					scheduleAttempt(attempt, BOOT_ACCOUNT_PERSISTENT_RETRY_MS);
				};
				const runRetryOnce = async (attempt) => {
					if (this._bootAccountOnlineRetryCancelled || !this._bootAccountOnlineRetryHandler) {
						return; // already ran via the other trigger
					}
					cleanup();
					try {
						await this.loadBootAccountContext();
						this.retryBootAccountDeferredNavigation();
					} catch (error) {
						if (this._bootAccountOnlineRetryCancelled) {
							return;
						}
						if (isUnauthenticatedError(error)) {
							// LSP-19862: the token can expire while the tab waits offline; the reconnect then
							// drives getMe straight into UnauthenticatedError. Same expired-session outcome as
							// startup — session-lost banner, warn, no Datadog error.
							this.handleBootAccountLoadUnauthenticated(error, { phase: "retry" });
							return;
						}
						if (!this.isBootAccountLoadTransientError(error)) {
							this.bootErrorMessage = "We couldn't load your account. Please refresh and try again.";
							logError("App: boot account-load retry failed", error);
							return;
						}

						if (attempt >= maxAttempts) {
							logWarn("App: transient boot account-load retry failed; timed attempt cap reached; retrying periodically", {
								classification: "transient",
								retryable: true,
								attempt,
								maxAttempts,
								nextDelayMs: BOOT_ACCOUNT_PERSISTENT_RETRY_MS
							});
							schedulePersistentOnlineAttempt(attempt);
							return;
						}

						const nextAttempt = attempt + 1;
						logWarn("App: transient boot account-load retry failed; retrying", {
							classification: "transient",
							retryable: true,
							attempt,
							maxAttempts,
							nextAttempt,
							nextDelayMs: BOOT_ACCOUNT_RETRY_DELAYS_MS[nextAttempt - 1]
						});
						scheduleTimedAttempt(nextAttempt);
					}
				};

				scheduleTimedAttempt(1);
			},
			async loadBootAccountForStartup() {
				// LSP-19862: gate on the authentication state, NOT on the route name. The Auth0 redirect
				// target (/callback?code=…&state=…), crawlers and plain logged-out visitors all boot with
				// isAuthenticated === false and share one root cause — getMe cannot succeed without an
				// access token — so a route-name allowlist would only ever cover some of them. Attempting
				// the load here and then reporting the inevitable UnauthenticatedError as a terminal boot
				// failure shipped an error to Datadog on every login while the login itself succeeded.
				if (!this.isAuthenticated) {
					// The unauthenticated share-link visitor used to reach EntityDetailPage only through the
					// getMe failure path; keep that setup when the load is skipped.
					this.handleBootShareLinkAccountLoadFailure();
					logInfo("App: boot account-load skipped — not authenticated", {
						routeName: this.$route?.name ?? null,
						correlationId: "boot-account-load-startup"
					});
					return;
				}
				try {
					await this.loadBootAccountContext();
				} catch (error) {
					if (this.handleBootShareLinkAccountLoadFailure()) {
						return;
					}
					if (isUnauthenticatedError(error)) {
						// The token can expire between the isAuthenticated() check above and getMe.
						this.handleBootAccountLoadUnauthenticated(error, { phase: "startup" });
						return;
					}
					if (this.isBootAccountLoadTransientError(error)) {
						this.bootErrorMessage = "We can't reach the service right now. Check your connection and try again.";
						this.scheduleBootAccountOnlineRetry();
						return;
					}
					this.bootErrorMessage = "We couldn't load your account. Please refresh and try again.";
					logError("App: boot account-load failed during startup", error, {
						classification: "terminal-or-unknown",
						correlationId: "boot-account-load-startup",
						retryable: false
					});
					return;
				}
				this.retryBootAccountDeferredNavigation();
			},
			async onWelcomeWizardCompleted(updatedOrganization) {
				// Merge the wizard response into this.me synchronously so showWelcomeWizard
				// flips to false even if the subsequent refreshMe fails — otherwise the
				// persistent dialog re-shows and the user is trapped.
				if (this.me && updatedOrganization) {
					this.me = {
						...this.me,
						organization: { ...this.me.organization, ...updatedOrganization }
					};
				}
				const successMessage = "You're all set! Welcome to LiveSwitch.";
				this.toast.success(successMessage, { timeout: calculateTimeout(successMessage) });
				try {
					this.me = await api.refreshMe();
					// Force a fresh JWT so authSession.organizationName (read from the
					// token's organization_name claim) reflects the new name immediately
					// — getAccessToken returns the cached token otherwise.
					await this.$auth._silentRefresh();
				} catch (error) {
					console.error("Failed to refresh after welcome wizard", error);
				}
			},
			isNavItemActive(item) {
				const to = this.resolveItemTo(item);
				if (!to) {
					return false;
				}

				const targetPath = this.$router.resolve(to).fullPath;
				const hash = this.$route.hash;
				// LSP-19475: /templates is an ALIAS of /sparks, and a Vue Router alias does not rewrite
				// the address bar. Without this, arriving on a bookmarked /templates URL leaves the
				// Sparks item unhighlighted even though it is the page being shown.
				let currentPath = normalizeLegacyRoutePath(this.$route.path);

				if (targetPath === "/") {
					return currentPath === "/";
				}

				if (hash) {
					currentPath += hash;
				}

				return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
			},
			async toggleNotifications(isOpen) {
				if (isOpen) {
					await this.getNotifications();
				}
			},
			onNotificationMenuUpdate(isOpen) {
				this.toggleNotifications(isOpen);
			},
			async clickNotificationAndUpdate(notification) {
				const redirectToUrl = await this.clickNotification(notification);
				if (redirectToUrl) this.nav(redirectToUrl);
				const id = this.notifications.findIndex((n) => n.id === notification.id);
				if (id !== -1 && !this.notifications[id].isRead) {
					this.notifications[id].isRead = true;
					this.unreadNotificationCount--;
				}
			},
			readNotification(notification) {
				readNotification(notification);
				const id = this.notifications.findIndex((n) => n.id === notification.id);
				if (id !== -1 && !this.notifications[id].isRead) {
					this.notifications[id].isRead = true;
					this.unreadNotificationCount--;
				}
			},
			async clearAllNotifications() {
				if (this.notifications.length === 0) return;
				if (
					await this.$root.$confirm.open(
						"Mark all notifications as read?",
						"This will mark every notification as read. You can't undo this.",
						false,
						"Mark all as read",
						"Cancel"
					)
				) {
					try {
						await api.clearAllNotifications();
						await this.getNotifications();
					} catch (err) {
						console.error("Failed to clear notifications", err);
					}
				}
			},
			getUserAvatarBackgroundColor() {
				return this.me?.account?.avatarColor;
			},
			getUserAvatarTextColor() {
				return getReadableInitialsColor(this.me?.account?.avatarColor);
			},
			// Handle authentication requests via BroadcastChannel
			async handleBroadcastAuthMessage(event) {
				console.log("[contact-admin] Received broadcast message:", event.data);

				// Only respond to scribe-auth-request messages
				if (event.data?.type !== "scribe-auth-request") {
					return;
				}

				console.log("[contact-admin] Handling scribe-auth-request via BroadcastChannel");
				try {
					// Get the current user's access token
					const token = await this.$auth.getAccessToken();
					console.log("[contact-admin] Got access token:", token ? "YES" : "NO");
					if (token && this.authChannel) {
						// Send the token back via BroadcastChannel
						console.log("[contact-admin] Sending token back via BroadcastChannel");
						this.authChannel.postMessage({
							type: "scribe-auth-token",
							token: token
						});
					} else {
						console.error("[contact-admin] No auth token available or no channel");
						if (this.authChannel) {
							this.authChannel.postMessage({
								type: "scribe-auth-token",
								error: "Authentication failed"
							});
						}
					}
				} catch (error) {
					console.error("[contact-admin] Failed to handle auth message:", error);
					// Send an error response to indicate authentication failed
					if (this.authChannel) {
						this.authChannel.postMessage({
							type: "scribe-auth-token",
							error: "Authentication failed"
						});
					}
				}
			},
			goToNotificationsPage() {
				this.nav("/notifications");
			},
			async redirectToHome() {
				// "Sign In" from the public share view (allowProjectPage) — preserve the
				// share path through Auth0 so the user lands back on the project they
				// were viewing instead of /. The callback handler validates the path
				// via isShareLinkPath before restoring, so it's safe to pass through.
				const sharePath = this.projectId && this.shareId ? `/projects/${this.projectId}?shareId=${this.shareId}` : null;
				// EXPLICIT: the user pressed "Sign In" on the public share view (LSP-17342).
				await this.$auth.login(sharePath ? { path: sharePath } : {}, false, null, {}, { isExplicitUserIntent: true });
			},
			async dismiss() {
				this.deferredPrompt = null;
			},
			async getNotifications() {
				// LSP-22341: boot, socket events, opening the bell and org switches all refresh without waiting for each
				// other. A response is applied unless a newer one was already applied (a newer request that fails must not
				// discard an older success), and loading ends once data lands or no refresh is still in flight.
				const requestId = (this._notificationsRequestId ?? 0) + 1;
				this._notificationsRequestId = requestId;
				// The token (and so the data) belongs to the organization current when the request starts.
				const requestOrganizationId = this.authSession?.organizationId ?? null;
				this._notificationsInFlight = (this._notificationsInFlight ?? 0) + 1;
				this.loadingNotifications = true;
				try {
					const notificationsPromise = api.getNotifications();
					const unreadNotificationCountPromise = api.getNotificationsUnreadCount();
					const [notifications, unreadNotificationCount] = await Promise.all([
						notificationsPromise,
						unreadNotificationCountPromise
					]);
					const currentOrganizationId = this.authSession?.organizationId ?? null;
					if (requestOrganizationId && currentOrganizationId && requestOrganizationId !== currentOrganizationId) {
						logInfo("App: dropping a notifications response for an organization the user switched away from", {
							requestId,
							requestOrganizationId,
							currentOrganizationId
						});
						return;
					}
					if (requestId < (this._notificationsAppliedRequestId ?? 0)) {
						logInfo("App: dropping a notifications response older than the one already shown", {
							requestId,
							appliedRequestId: this._notificationsAppliedRequestId
						});
						return;
					}
					this._notificationsAppliedRequestId = requestId;
					this.notifications = notifications.rows;
					this.totalNotifications = notifications.count;
					this.unreadNotificationCount = unreadNotificationCount.count;
					this.loadingNotifications = false;
				} catch (error) {
					console.error("Error getting notifications", error);
				} finally {
					this._notificationsInFlight -= 1;
					if (this._notificationsInFlight === 0) {
						this.loadingNotifications = false;
					}
				}
			},
			async markAsRead(notification) {
				await api.updateNotification(notification.id, { isRead: true });
				this.getNotifications();
			},
			activateSubscription() {
				const email = encodeURIComponent(this.encrypt(JSON.parse(StorageWrapper.getItem("user")).email));
				const organizationId = encodeURIComponent(StorageWrapper.getItem("organization_id"));
				window.location.href = `${window.env.VITE_PAY_DOMAIN}?skipAccountCreation=true&identityToken=${StorageWrapper.getItem(
					"platform_identity_token"
				)}&tenantId=${organizationId}&email=${email}`;
			},
			openUpgradeDialog() {
				this.$root.$freeTrialEndedDialog.open(this.me?.organization);
			},
			async upgrade() {
				const redirected = await this.navigateToBillingPortal();
				if (redirected) {
					return;
				}

				if (window.env.VITE_ENABLE_PURCHASE_MODULE == "false") {
					window.open(
						"mailto:" +
							window.env.VITE_SUPPORT_EMAIL +
							"?subject=" +
							encodeURIComponent("Subscription Request") +
							"&body=" +
							encodeURIComponent("I would like to subscribe to LiveSwitch.")
					);
				} else {
					this.nav(this.subscriptionUrl);
				}
			},
			async navigateToBillingPortal(billingPortalHome = false) {
				if (this.isNewSubscription) {
					const isTrialState =
						this.subscription.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL_EXPIRED ||
						this.subscription.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL;

					if (
						!billingPortalHome &&
						isTrialState &&
						!this.subscription.hasPaymentMethod &&
						!this.subscription.isInPendingPaymentGracePeriod
					) {
						this.nav("/pricing");
						return true;
					}

					try {
						let flow = undefined;

						if (!billingPortalHome) {
							flow = "subscription_update";

							if (isTrialState) {
								if (!this.subscription.hasPaymentMethod) {
									flow = "payment_method_update";
								} else {
									flow = "";
								}
							}

							if (this.subscription.isInPendingPaymentGracePeriod) {
								flow = "payment_method_update";
							}
						}

						const billingPortalResponse = await api.getBillingPortalSessionUrl(flow, window.location.toString());

						window.location = billingPortalResponse.url;
					} catch (err) {
						console.error(err, {
							extra: {
								subscriptionId: this.subscription.id
							},
							tags: {
								method: "navigateToBillingPortal"
							}
						});
						window.location = `${window.env.VITE_STRIPE_URL}?prefilled_email=${this.me.user.email}`;
					}

					return true;
				}

				return false;
			},
			encrypt(value) {
				return crypto.encrypt(value.trim(), window.env.VITE_CONTACT_ENCRYPTION_KEY).toString();
			},
			async storeAndRefresh() {
				// Deliberately not awaited — see the break-it round 12 comment in storeUserAccount() for why
				// this path must not block navigation on it. The `.catch` only prevents an unhandled rejection;
				// storeAndRefresh still navigates immediately either way.
				this.storeUserAccount().catch((error) => logError("App: storeUserAccount failed during storeAndRefresh", error));
				window.location.href = "";
			},
			async storeUserAccount() {
				// load self - true means include permissions for the user
				//this.me = await api.getMe();
				// Capture account state before awaiting AppCues because another refresh can replace `this.me` while this call is suspended.
				const appcuesMe = this.me;
				const appcuesOrganizationName = this.organizationName;

				// Written BEFORE the AppCues block because that block now awaits the vendor's
				// identify()/group() promises. `storeAndRefresh()` does not await this method, so it reaches
				// `window.location.href = ""` while this method is suspended on those awaits — and with a
				// hanging vendor promise that suspension lasts until the 2000ms bound (twice, for
				// identify + group). Left below the awaits this marker was simply never written on that
				// path, and internal traffic kept counting towards Google Analytics. Hoisting it keeps the
				// write on the synchronous prefix both callers are guaranteed to run, without making
				// `storeAndRefresh` block navigation on a vendor promise. // break-it round 12
				if (appcuesMe?.organization?.isTestAccount) {
					// This will hide future data from Google Analytics for this computer.
					// Not a big deal if it fails. No point in using StorageWrapper here as the other side runs before app loads.
					// If the user doesn't support LocalStorage the other side won't work.
					try {
						localStorage.setItem("traffic_type", "internal");
					} catch (e) {
						console.info("Current browser does not support local storage.", e);
					}
				}

				// this account wrapper matches up to what we have
				// on the server so we can query permissions easily
				this.account = new Account(this.me.account);
				StorageWrapper.setItem("account", JSON.stringify(this.me.account));
				if (this.me.account) {
					/*const response  = await api.createPlatformIdentity(this.me.account.userId);
	       			api.setPlatformIdentityToken(response?.token);*/
					//StorageWrapper.setItem('pit_response', response);
					//StorageWrapper.setItem('platform_identity_token', response?.token);
					//const status = this.getSubscriptionStatus(response.applicationId, response.applications);

					// we also have organization.isTrial - that is used to indicate
					// that this org has had a trial at some point
					// and NOT to indicate if that trial is currently active or not
					this.isTrial = this.me.organization.isTrial;
					this.isTrialCCNow = this.me.organization.isTrialCCNow;

					this.isActiveTrial = this.me.organization.isActiveTrial;
					this.trialDaysRemaining = this.me.organization.trialDaysRemaining;
					this.isEndedTrial = this.me.organization.isEndedTrial;

					// paid account info
					this.isSubscriptionActive = this.me.organization.isSubscriptionActive;
					this.isSubscriptionOwner = this.me.account.isSubscriptionOwner;

					// Subscription Management - new flow
					this.subscription = this.me.organizationSubscription;
					this.isNewSubscription = isNewSubscription(this.subscription);

					if (this.subscription) {
						// Trial-extended orgs have a stale org-level trialDaysRemaining; for new subscriptions the
						// subscription's own countdown is the source of truth. Resolve it (and the trial-state flags)
						// synchronously here so the Appcues identify below reports the real values rather than the
						// stale org-derived ones — these have no DOM dependency, so they don't need $nextTick (LSP-15991).
						this.trialDaysRemaining = effectiveTrialDaysRemaining(this.subscription, this.trialDaysRemaining);
						this.isTrial =
							this.subscription.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL ||
							this.subscription.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL_EXPIRED;
						this.isActiveTrial =
							this.subscription.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL && this.trialDaysRemaining > -1;
						this.isEndedTrial =
							this.subscription.accountStatus === constants.SUBSCRIPTION_STATUS.TRIAL_EXPIRED || this.trialDaysRemaining < 0;
						this.isSubscriptionActive = this.subscription.accountStatus === constants.SUBSCRIPTION_STATUS.ACTIVE;

						// Wait for refs to exist before pushing subscription info into the banner / ended dialog.
						this.$nextTick(() => {
							this.$root.$trialTopBar = this.$refs.trialTopBar;

							// Only set the subscription info if the subscription is not 'active'
							if (this.subscription.accountStatus !== constants.SUBSCRIPTION_STATUS.ACTIVE) {
								this.$root.$freeTrialEndedDialog.setSubscriptionInfo(
									this.subscription,
									this.me.user.email,
									this.trialDaysRemaining
								);
								this.$root.$trialTopBar.setSubscriptionInfo(this.subscription);
							}
						});
					}

					// LSP-18034: index.html's vendor gate deliberately does not load the Appcues script
					// off prod/staging (localhost/dev/testing) or on the /form + /share pages, so
					// `window.Appcues` is legitimately absent there. That gate is the single source of
					// truth for where Appcues runs, so this consumer keys off its outcome — the global's
					// presence — rather than duplicating its hostname regex, which would silently drift.
					// Calling identify() with no global threw on every local/dev/testing boot and logged
					// "Error initializing AppCues", which reads like a real failure and polluted local and
					// CI consoles (including local Playwright journey runs). A genuine failure while
					// Appcues IS loaded still reaches console.error in the catch below.
					//
					// break-it round 1: the presence check and the global read stay INSIDE this try, and
					// the global is read exactly once. Reading `window.Appcues` can itself throw when a
					// privacy/content-blocker extension replaces the property with a throwing accessor;
					// before this guard existed that throw was contained here. Gating outside the try
					// would let it escape storeUserAccount() and skip everything after this block, and reject
					// the returned promise. Note that only the account-loading callers await that promise;
					// `storeAndRefresh` does NOT, which is why the isTestAccount traffic_type write is hoisted
					// above this block rather than left after it. // break-it round 12
					//
					// break-it round 3: "the global is absent" alone is NOT a faithful proxy for "the gate
					// blocked AppCues here". On prod/staging the gate ALLOWS AppCues and injects the loader
					// with `async = true`, so an absent global there means a genuinely lost identify/group
					// (loader 404, CSP/network block, privacy extension, or the script simply not landed
					// yet — nothing retries). Silencing that to info would hide a real prod failure that
					// used to be visible as an error. The gate publishes its own synchronous "allowed here"
					// marker immediately before injecting the loader — `window.AppcuesSettings`
					// (index.html) — so the two cases are told apart with the gate's own signal, still
					// without duplicating its hostname regex.
					//
					// LSP-18056: identify() and group() may return promises, so their settlement must
					// be observed inside the existing error boundary. A rejection is a proven failure
					// while AppCues is loaded and therefore keeps the existing console.error report. A
					// timeout is only an unknown outcome because the vendor promise may be slow, so it
					// stays at console.info and deliberately does not claim initialization. Bounding the
					// wait prevents a hanging vendor promise from stalling app boot for the callers that DO
					// await storeUserAccount(). Non-thenable SDK calls keep the existing immediate path
					// without creating a timer.
					//
					// break-it round 6: each call now reports its own timeout before the next call runs, and
					// a rejection arriving after that timeout is still reported exactly once. This preserves
					// identify's timeout evidence when group subsequently rejects.
					//
					// LSP-22199: Appcues bills by monthly active users and only identified users count, so identify()
					// and group() are skipped TOGETHER for orgs the policy leaves out (staging, E2E and test accounts;
					// see helpers/appcuesIdentifyPolicy.ts). A skipped org also clears any user Appcues already holds
					// (clearAppcuesIdentity), and a script that has not landed yet cannot raise the "never loaded" error
					// below because a skipped org does not wait for it. The profile menu notice and the clearing both
					// apply only when the index.html gate allowed Appcues on this host (AppcuesSettings): on
					// localhost/dev/testing Appcues is off for a different reason and there is nothing to clear.
					this.appcuesSkipReason = null;
					this.appcuesLatestCallSkipped = false;
					const appcuesDecision = getAppcuesIdentifyDecision(appcuesMe?.organization);
					try {
						const appcues = appcuesDecision.identify ? window.Appcues : undefined;
						if (!appcuesDecision.identify) {
							const gateAllowedAppcues = Boolean(window.AppcuesSettings);
							if (gateAllowedAppcues) {
								this.appcuesSkipReason = appcuesDecision.reason;
								// Skipping identify() alone leaves any earlier user in Appcues' browser storage, so a returning
								// browser (or an in-page switch from an allowed org) would keep being tracked and counted.
								this.appcuesClearCount = (this.appcuesClearCount ?? 0) + 1;
								this.appcuesLatestCallSkipped = true;
								clearAppcuesIdentity();
							}
							console.info("AppCues identify/group skipped: " + appcuesDecision.reason, {
								organizationId: appcuesMe?.organization?.id,
								accountId: appcuesMe?.account?.id,
								gateAllowedAppcues
							});
						} else if (!appcues) {
							if (window.AppcuesSettings) {
								console.error(
									"Error initializing AppCues: the vendor gate allowed AppCues on this host (AppcuesSettings is set) but window.Appcues never loaded — identify/group were skipped for this page load."
								);
							} else {
								console.info(
									"AppCues is not loaded on this environment (the index.html vendor gate blocks it off prod/staging and on the /form + /share pages); skipping identify/group."
								);
							}
						} else {
							const clearCountAtStart = this.appcuesClearCount ?? 0;
							const identifySettled = await settleAppcuesCall(
								appcues.identify(appcuesMe.account.id, {
									// recommended (optional) properties
									createdAt: new Date(appcuesMe.account.createdAt).getTime(), // Unix timestamp of user signup date

									role: appcuesMe.account?.roles[0]?.name, // Current user’s role or permissions
									accountId: appcuesMe.organization?.id, // Current user's account ID
									firstName: appcuesMe.user.firstName,
									isTrial: this.isTrial,
									isTrialCCNow: this.isTrialCCNow,

									isActiveTrial: this.isActiveTrial,
									trialDaysRemaining: this.trialDaysRemaining,
									isEndedTrial: this.isEndedTrial,

									isSubscriptionActive: this.isSubscriptionActive,
									isSubscriptionOwner: this.isSubscriptionOwner,

									// additional suggestions
									companyName: appcuesOrganizationName, // Current user’s company name
									email: appcuesMe.user.email, // Current user's email
									industry: (appcuesMe.organization?.Industries ?? []).map((i) => i.name),
									country: appcuesMe.organization?.defaultCountryCode ?? null
								}),
								"identify"
							);

							let groupSettled = true;
							// identify() can still be pending when the user switches to a skipped org, which clears the identity.
							// Grouping afterwards would re-register the user, so the older call stops here.
							if ((this.appcuesClearCount ?? 0) !== clearCountAtStart) {
								groupSettled = false;
								// That older identify() may also have written the user back after the clear (the SDK stores it when
								// the call lands), so clear again. Only when the NEWEST call is a skipped org: if the user has since
								// moved on to an allowed org, that call identified itself and its identity must be left alone.
								const clearedAgain = Boolean(this.appcuesLatestCallSkipped);
								if (clearedAgain) clearAppcuesIdentity();
								console.info("AppCues group skipped: the identity was cleared by a switch to an org where Appcues is off", {
									organizationId: appcuesMe?.organization?.id,
									accountId: appcuesMe?.account?.id,
									clearedAgain
								});
							} else if (appcuesMe.organization) {
								groupSettled = await settleAppcuesCall(
									appcues.group(appcuesMe.organization.id, {
										// example properties
										createdAt: new Date(appcuesMe.organization.createdAt).getTime(), // Unix timestamp of account signup date
										companyName: appcuesOrganizationName, // Account's name
										industry: (appcuesMe.organization.Industries ?? []).map((i) => i.name),
										country: appcuesMe.organization.defaultCountryCode ?? null
									}),
									"group"
								);
							}
							if (identifySettled && groupSettled) {
								console.info("AppCues initialized");
							}
						}
					} catch (err) {
						console.error("Error initializing AppCues", err);
					}
				}
			},
			attributesEnabled() {
				// if the feature is enabled, show the attributes
				if (
					this.me &&
					this.me.organization.OrganizationFeatures &&
					this.me.organization.OrganizationFeatures.find((f) => {
						return f.featureName == constants.FEATURES.ATTRIBUTES;
					})
				) {
					return true;
				}
				// otherwise, fall back to the config
				if (window.env.VITE_ENABLE_ATTRIBUTES == "false") {
					return false;
				}
				return true;
			},
			async connectAccount(href) {
				let width = 1200;
				let height = 600;
				let top = window.outerHeight / 2 - height / 2;
				let left = window.outerWidth / 2 - width / 2;
				let params = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=${width},height=${height},left=${left},top=${top}`;
				let w = window.open(href, "sso", params);
				if (!w) {
					// LSP-15319: this was the worst shape of the silent failure. `w` was never
					// checked, so on a blocked popup (or an exhausted tab limit — iOS Safari caps
					// around 500, indistinguishable from blocking and identical in effect) every
					// tick below threw on `w.location.hash` into the empty catch, meaning the
					// interval was never cleared and polled forever at 250ms for the rest of the
					// session. Refuse to start the interval at all and tell the user why.
					logWarn("App.connectAccount: window.open returned null — popup blocked or tab limit reached", {
						hasHref: !!href
					});
					this.toast.error(POPUP_BLOCKED, { timeout: calculateTimeout(POPUP_BLOCKED) });
					return;
				}
				let interval = window.setInterval(() => {
					try {
						let hash = w.location.hash;
						if (hash.indexOf("ls-contact-done") > -1) {
							// LSP-15861: mark this as a toast (not the not-connected install prompt) so
							// the permanent-dismiss gate and "Don't show again" button never apply to
							// it — those are install-prompt-only. Reusing the same snackbar object
							// means a later possiblyShowPluginInstallations() call must fully
							// reset the fields this path touches (kind/text/showAction/timeout).
							this.pluginInstallation.kind = "toast";
							if (hash.indexOf("-error") > -1) {
								const errorDescription = new URL(w.location.href).searchParams.get("error_description")?.trim();
								this.pluginInstallation.text = errorDescription
									? "Failed to connect your account! The response was: " + errorDescription
									: "Failed to connect your account. Please try again.";
								this.pluginInstallation.showAction = true;
							}
							if (hash.indexOf("-success") > -1) {
								this.handlePluginConnectSuccess();
							}
							w.close();
							window.clearInterval(interval);
						}
					} catch (e) {
						//console.log(e)
					}
				}, 250);
			},
			// LSP-15861 review: the connect popup's success branch used to only swap the toast copy,
			// leaving this.me (and so me.pluginComponentInstallations) stale. The $route.name watcher
			// calls possiblyShowPluginInstallations() on reaching /integrations, which reads that list
			// via findUnconnectedPluginInstallation() — so a just-connected plugin still looked
			// unconnected, resurfacing the banner and never letting resetPluginInstallBannerIfSet fire.
			// Refresh me so subsequent navigations see the new OAuth. Deliberately does NOT chain
			// possiblyShowPluginInstallations(): another still-unconnected plugin would immediately
			// overwrite the success toast the user is meant to read.
			handlePluginConnectSuccess() {
				this.pluginInstallation.text = "You have successfully connected your account!";
				this.pluginInstallation.showAction = false;
				this.pluginInstallation.timeout = 3500;
				// Pin the org: a popup opened in org A can resolve after the user switched to org B, and
				// assigning A's `me` over B's would corrupt the active context (same guard as the
				// banner-state writes).
				const connectedOrganizationId = this.currentOrganizationId ?? null;
				// Latch so a navigation to /integrations mid-refresh can't render a prompt from stale data.
				this._pluginConnectRefreshInFlight = true;
				return api
					.refreshMe()
					.then((refreshed) => {
						if ((this.currentOrganizationId ?? null) !== connectedOrganizationId) {
							logWarn("App: skipping post-connect me refresh — active org changed while the popup resolved", {
								connectedOrganizationId,
								activeOrganizationId: this.currentOrganizationId ?? null
							});
							return;
						}
						this.me = refreshed;
						// Now that `me` is fresh, if NOTHING is left needing authorization the prior
						// dismissal/acknowledgement is fully resolved — clear it so a later disconnect re-arms the
						// banner. possiblyShowPluginInstallations is the only other caller of this reset and the
						// $route.name watcher early-returns unless the route is `integrations`, so without this the
						// mute could persist for the whole session after a banner-driven connect. Gated on the same
						// condition possiblyShowPluginInstallations uses, because the reset itself does not check it —
						// connecting plugin A while plugin B is still unconnected must NOT clear the mute.
						if (!this.findUnconnectedPluginInstallation()) {
							this.resetPluginInstallBannerIfSet?.();
						}
					})
					.catch((error) => {
						// A failed refresh leaves the just-connected plugin looking unconnected, which can
						// resurface the prompt and skip the reset. Retry once before giving up; the org guard is
						// re-checked because the user may have switched during the first attempt.
						logWarn("App: post-connect me refresh failed; retrying once", { error });
						return api
							.refreshMe()
							.then((retried) => {
								if ((this.currentOrganizationId ?? null) !== connectedOrganizationId) return;
								this.me = retried;
								if (!this.findUnconnectedPluginInstallation()) {
									this.resetPluginInstallBannerIfSet?.();
								}
							})
							.catch((retryError) => {
								logError(
									"App: post-connect me refresh failed after retry — banner state re-syncs on next boot",
									retryError
								);
							});
					})
					.finally(() => {
						this._pluginConnectRefreshInFlight = false;
					});
			},
			getSubscriptionStatus(applicationId, applications) {
				const obj = JSON.parse(applications);
				return obj[applicationId][0].linkStatus;
			},
			// LSP-15861: was `this.me.pluginComponentInstallations[0]` inside the loop — always
			// re-read the FIRST element regardless of the loop index `i`, so a Podium (or any
			// plugin) installation that needed auth was silently missed whenever an earlier,
			// already-connected installation sorted first. `.find()` reads the right element.
			findUnconnectedPluginInstallation() {
				if (!this.me?.pluginComponentInstallations) {
					return null;
				}
				return (
					this.me.pluginComponentInstallations.find(
						(installation) => installation.PluginComponentInstallationAccountOAuths.length === 0
					) || null
				);
			},
			possiblyShowPluginInstallations() {
				// A post-connect me refresh is in flight: this.me still lists the just-connected plugin as
				// unconnected, so proceeding would overwrite the "successfully connected" toast with a
				// sticky (timeout -1) install prompt for an account that IS connected. Skip; the refresh
				// completes and the next natural trigger (boot / org switch / Integrations navigation)
				// re-evaluates against accurate data.
				if (this._pluginConnectRefreshInFlight) {
					logWarn("App: possiblyShowPluginInstallations skipped — post-connect me refresh still in flight");
					return;
				}
				// Not logged: "no unconnected installation" is the default/expected state for the
				// overwhelming majority of orgs (no Podium installed, or already connected) — it
				// fires on essentially every boot and every org switch, so a diagnostic log here
				// would be constant noise rather than a signal. The gate conditions below are
				// logged because they fire only for the much smaller population that has actually
				// dismissed the banner, where "why isn't this showing" is a real support question.
				// Clear any stale install-prompt still on screen from a previous org/state before
				// deciding whether THIS call should show one — otherwise an early-return suppression
				// path below (all-connected, dismissed-off-Integrations, once-per-session-consumed)
				// would leave the previous org's prompt visible after an org switch. Only touches the
				// install-prompt; the shared connect success/failure toast (kind "toast") is left alone.
				if (this.pluginInstallation.kind === "install-prompt") {
					this.pluginInstallation.visible = false;
				}

				const needsAuthorization = this.findUnconnectedPluginInstallation();
				if (!needsAuthorization) {
					// Everything the user could be prompted about is now connected, so a prior
					// dismissal/acknowledgement is fully resolved — clear it so a LATER disconnect
					// re-arms the banner (LSP-15861: the dismissal is per unresolved-connection, not
					// a permanent forever-mute). Fire-and-forget background sync.
					this.resetPluginInstallBannerIfSet();
					return;
				}

				// AC5 / Codex correction: once the user has permanently dismissed or
				// acknowledged the prompt, it never shows again EXCEPT as a once-per-session
				// in-context reminder on the Integrations/Apps page itself. State lives on the
				// current-org Account (LSP-15861), so switching orgs reads that org's own value.
				const bannerState = this.me?.account?.pluginInstallBannerState;
				const isIntegrationsRoute = this.$route?.name === "integrations";
				if (bannerState === "dismissed" || bannerState === "acknowledged") {
					if (!isIntegrationsRoute) {
						logWarn(
							"App: possiblyShowPluginInstallations suppressed — banner previously dismissed and not on Integrations route",
							{
								bannerState,
								routeName: this.$route?.name
							}
						);
						return;
					}
					const activeAccountId = this.me?.account?.id ?? null;
					if (activeAccountId && this.pluginBannerReminderShownAccountIds.includes(activeAccountId)) {
						logWarn("App: possiblyShowPluginInstallations suppressed — already shown once on Integrations this session", {
							bannerState
						});
						return;
					}
					// The once-per-session flag is set AFTER the snackbar is shown (below), not here —
					// otherwise a throw during OAuth URL construction would consume the single
					// reminder without it ever appearing.
				}

				// ok, we have one that needs auth. get the details on how to perform the auth
				let component = needsAuthorization.PluginComponent;
				let plugin = component.Plugin;
				let oauth = component.configuration.oauth;
				let state = {};
				oauth.state.forEach((s) => {
					state[s.name] = s.value;
				});
				//FUTURE: support for other auth types
				let callbackUrl = "https://" + window.location.host + "/plugin/callback.html?plugin=" + plugin.providerName;
				let href = parser.parse(oauth.authorizeUrl, {
					redirectUri: encodeURIComponent(callbackUrl),
					clientId: encodeURIComponent(oauth.clientId),
					scope: encodeURIComponent(oauth.scope.join(" ")),
					// FUTURE: s.value could be more than just a string...is that a problem?
					state: encodeURIComponent(JSON.stringify(state))
				});

				// LSP-15861: set every field the reused success/failure toast path (connectAccount)
				// can mutate. The snackbar object is shared between the two purposes, so without a
				// full reset here a stale toast state (kind/showAction/timeout) could leak into a
				// later install-prompt fire — e.g. connect-success shortens the timeout to 3500ms,
				// which must not carry over to the next org-switch's install prompt.
				this.pluginInstallation.kind = "install-prompt";
				this.pluginInstallation.color = "warning";
				this.pluginInstallation.icon = "icon-warning-triangle";
				this.pluginInstallation.title = "Almost there!";
				this.pluginInstallation.timeout = -1;
				this.pluginInstallation.showAction = true;
				this.pluginInstallation.text = `${component.displayName} is installed but not connected yet.`;
				this.pluginInstallation.href = href;
				this.pluginInstallation.visible = true;
				// Consume the once-per-session Integrations reminder only now that the banner is
				// actually visible. bannerState is truthy only in the suppressed-but-reminding case.
				if (bannerState === "dismissed" || bannerState === "acknowledged") {
					const shownAccountId = this.me?.account?.id ?? null;
					if (shownAccountId && !this.pluginBannerReminderShownAccountIds.includes(shownAccountId)) {
						this.pluginBannerReminderShownAccountIds.push(shownAccountId);
					}
				}
			},
			// LSP-15861: "Don't show again" — optimistic hide + local state set, persisted
			// server-side so the dismissal survives logout/login and follows the user across
			// devices (AC2). Reverts on failure (banner may reappear next boot; acceptable —
			// see plan risk notes). State is per-org via Account (not the identity-global User),
			// so switching orgs no longer wipes another org's dismissal.
			async dismissPluginInstallBannerForever() {
				this.pluginInstallation.visible = false;
				// Capture the exact account object we mutate: a me-refresh (boot retry, org switch)
				// can replace this.me.account while the persist is in flight, and the failure path
				// must not clobber that fresh server state with our stale pre-dismiss value.
				const mutatedAccount = this.me?.account ?? null;
				// Defense-in-depth (Bugbot, PR #2980): never downgrade an existing state. The
				// once-per-session Integrations reminder fires with state "dismissed"/
				// "acknowledged"; persisting "dismissed" from it would overwrite "acknowledged"
				// and resurrect the sidebar badge the user just cleared. The template already
				// hides the button in that state — this guard covers any path that still lands
				// here (stale render, programmatic call). Hide-only, no PUT.
				if (!mutatedAccount || mutatedAccount.pluginInstallBannerState) {
					return;
				}
				const previousBannerState = mutatedAccount.pluginInstallBannerState ?? null;
				// Pin the org too, not just the account object: during a switch the token-derived
				// currentOrganizationId flips BEFORE refreshMe replaces me.account, so an in-flight write
				// that correctly 400s on expectedAccountId would otherwise still look 'active' here and
				// roll back + toast against the org the user has already moved to.
				const dismissOrganizationId = this.currentOrganizationId ?? null;
				// If the user is ALREADY on the Integrations page there is nothing left to nudge them
				// toward, so record "acknowledged" rather than "dismissed" — otherwise the sidebar badge
				// (driven by the "dismissed" state) would pop up pointing at the very page they're on and
				// stay until they navigate away and back (the route watcher only fires on route CHANGE).
				const targetState = this.$route?.name === "integrations" ? "acknowledged" : "dismissed";
				mutatedAccount.pluginInstallBannerState = targetState;
				try {
					await (this.persistBannerStateSerialized?.(targetState) ??
						api.updateAccountBannerState(targetState, mutatedAccount.id));
				} catch (error) {
					logError("App: failed to persist permanent plugin-install-banner dismissal", error);
					// Roll back AND toast ONLY when (a) the account object we mutated is still the
					// active one (same reference — a replaced me.account carries authoritative server
					// state) and (b) it still holds our optimistic "dismissed" (something else
					// rewriting the value mid-flight owns the newer truth; don't undo it). Bugbot
					// (PR #2980, M7): the same condition must gate the toast — telling the user "we
					// couldn't save that" is misattributed noise when they've already switched orgs or
					// another write already superseded this one.
					const isStillTheActiveDismissAttempt =
						mutatedAccount &&
						this.me?.account === mutatedAccount &&
						(this.currentOrganizationId ?? null) === dismissOrganizationId &&
						mutatedAccount.pluginInstallBannerState === targetState;
					if (isStillTheActiveDismissAttempt) {
						mutatedAccount.pluginInstallBannerState = previousBannerState;
						this.toast.error("We couldn't save that. Please try again.");
					}
				}
			},
			// LSP-15861: acknowledges a "dismissed" banner state when the user reaches the
			// Integrations page. Fired from three call sites (the "$route.name" watcher for
			// in-app navigation, loadBootAccountContext for a direct URL load of /integrations,
			// and refreshCurrentUserContextForOrgSwitch for an org switch while already on the
			// page). Only auto-acknowledges a "dismissed" state (AC4) — a visit BEFORE the user
			// has ever dismissed the banner (bannerState === null) deliberately does NOT
			// pre-clear anything, because the badge's whole purpose is "you dismissed the
			// reminder; here's where it still lives" — there is nothing to acknowledge yet.
			async acknowledgePluginInstallBannerIfDismissed() {
				if (!this.me?.account || this.me.account.pluginInstallBannerState !== "dismissed") {
					return;
				}
				// In-flight latch: the optimistic mutation below already no-ops a repeat call via
				// the "dismissed" check, but a me-refresh landing mid-persist can reset the local
				// state back to "dismissed" and let an overlapping call double-PUT. One persist
				// at a time is enough — a missed acknowledge just re-syncs on the next visit.
				// Latch keyed by ACCOUNT id, not a shared boolean: an acknowledge still in flight for one org
				// must not make the next org's acknowledge return early and leave its badge up.
				const acknowledgeAccountId = this.me.account.id ?? null;
				if (this._pluginBannerAcknowledgeInFlightAccountId === acknowledgeAccountId) {
					// Bugbot (PR #2980, M3): silent early-return — log so a future "acknowledge never
					// persisted" report can be triaged from this log line instead of reconstructed
					// from indirect signals, matching the sibling org-mismatch skip in
					// persistBannerStateSerialized.
					logWarn("App: skipping acknowledge — already in flight for this account", { accountId: acknowledgeAccountId });
					return;
				}
				this._pluginBannerAcknowledgeInFlightAccountId = acknowledgeAccountId;
				// Optimistic: navigationItems is a computed over me.account.pluginInstallBannerState,
				// so this single mutation reactively clears the sidebar badge immediately.
				this.me.account.pluginInstallBannerState = "acknowledged";
				try {
					await (this.persistBannerStateSerialized?.("acknowledged") ??
						api.updateAccountBannerState("acknowledged", acknowledgeAccountId));
				} catch (error) {
					logError("App: failed to persist Integrations-visit plugin-install-banner acknowledgement", error);
					// Best-effort, not reverted: this is a low-stakes background sync (clearing a
					// badge), not a destructive action. Reverting would make an already-cleared
					// badge flicker back for the user; worst case it just re-syncs on next visit.
				} finally {
					// Bugbot (PR #2980, M9): only clear the latch if it still names THIS call's
					// account. A me-refresh/org-switch mid-flight can let a second call for a
					// DIFFERENT account overwrite the latch before this one's finally runs —
					// unconditionally nulling it here would clear that other account's still-in-flight
					// guard and let an overlapping call for it double-PUT.
					if (this._pluginBannerAcknowledgeInFlightAccountId === acknowledgeAccountId) {
						this._pluginBannerAcknowledgeInFlightAccountId = null;
					}
				}
			},
			// Serialize banner-state persists (dismiss / acknowledge / reset) through one per-instance
			// chain so they reach the server in CALL order — two overlapping PUTs (e.g. Don't-show-again
			// then an Integrations visit) must not be reordered by the network and leave the server on
			// the earlier value, resurrecting a badge the user cleared. onRejected keeps the chain alive.
			persistBannerStateSerialized(value) {
				// Capture the account this write was queued FOR. The PUT targets /accounts/self, which the
				// server resolves from the org token at request time — so a write still queued behind a slow
				// one when the user switches orgs would land on the NEW org's account and corrupt its state.
				// Skip instead: the value only matters for the org it was made in, and it re-syncs on boot.
				const queuedForAccountId = this.me?.account?.id ?? null;
				// Also pin the ORG the write was queued for. /accounts/self resolves its target from the
				// org access token that buildHeaders attaches when the request actually fires, and during an
				// org switch authSession.organizationId (and that token) update BEFORE refreshMe swaps in the
				// new `me` — so an account-id-only gate can pass with the stale account yet still PUT as the
				// new org. currentOrganizationId is token-derived, so comparing it closes that window.
				const queuedForOrganizationId = this.currentOrganizationId ?? null;
				const run = () => {
					const activeAccountId = this.me?.account?.id ?? null;
					const activeOrganizationId = this.currentOrganizationId ?? null;
					if (activeAccountId !== queuedForAccountId || activeOrganizationId !== queuedForOrganizationId) {
						logWarn("App: skipping queued banner-state persist — active org/account changed since it was queued", {
							queuedForAccountId,
							activeAccountId,
							queuedForOrganizationId,
							activeOrganizationId
						});
						return Promise.resolve();
					}
					// LSP-15861 (review H2): the client-side check above closes the window up to here,
					// but api.updateAccountBannerState still awaits a token refresh before the request
					// actually fires — if the org switches during THAT await, the request would
					// authenticate as the new org. Send the account id captured at enqueue time so the
					// server can reject deterministically if it no longer matches.
					return api.updateAccountBannerState(value, queuedForAccountId ?? undefined);
				};
				this._bannerStatePersistChain = (this._bannerStatePersistChain ?? Promise.resolve()).then(run, run);
				return this._bannerStatePersistChain;
			},
			async resetPluginInstallBannerIfSet() {
				// Called when NO installation needs authorization (everything connected). If the user
				// had previously dismissed or acknowledged the banner, clear that state back to null
				// (the column default) so a subsequent disconnect shows the banner again — the mute is
				// scoped to the connection the user dismissed, not forever (LSP-15861). State lives on
				// the current-org Account, so this reset only ever clears the CURRENT org's dismissal —
				// switching orgs no longer wipes a dismissal made in another org.
				if (!this.me?.account || !this.me.account.pluginInstallBannerState) {
					return;
				}
				// In-flight latch, same rationale as acknowledge: one persist at a time so a me-refresh
				// landing mid-persist can't drive an overlapping double-PUT. A missed reset just
				// re-syncs on the next boot/org-switch.
				// Latch keyed by ACCOUNT id (same rationale as acknowledge above).
				const resetAccountId = this.me.account.id ?? null;
				if (this._pluginBannerResetInFlightAccountId === resetAccountId) {
					// Bugbot (PR #2980, M3): silent early-return — log so a future "reset never
					// persisted" report can be triaged from this log line instead of reconstructed
					// from indirect signals, matching the sibling org-mismatch skip in
					// persistBannerStateSerialized.
					logWarn("App: skipping banner reset — already in flight for this account", { accountId: resetAccountId });
					return;
				}
				this._pluginBannerResetInFlightAccountId = resetAccountId;
				// Optimistic: navigationItems is a computed over me.account.pluginInstallBannerState, so
				// clearing it reactively drops the sidebar badge immediately.
				this.me.account.pluginInstallBannerState = null;
				try {
					await (this.persistBannerStateSerialized?.(null) ?? api.updateAccountBannerState(null, resetAccountId));
				} catch (error) {
					logError("App: failed to persist plugin-install-banner reset after all installations connected", error);
					// Best-effort background sync, not reverted: re-syncs on the next boot/org-switch.
				} finally {
					// Bugbot (PR #2980, M9): only clear the latch if it still names THIS call's
					// account — see the matching comment in acknowledgePluginInstallBannerIfDismissed.
					if (this._pluginBannerResetInFlightAccountId === resetAccountId) {
						this._pluginBannerResetInFlightAccountId = null;
					}
				}
			},
			async checkExchangeAuthHealth() {
				try {
					// LSP-16585: The Exchange failing-auths endpoint can report stale refresh
					// failures for platforms the org is not actually connected to. Cross-reference
					// against the installed apps so the banner only flags genuinely-connected
					// integrations that need reauthentication, matching the Integrations tab.
					//
					// Use allSettled so a getApps failure can't reject the batch and wrongly clear a
					// banner the failing-auths endpoint already reported (getAuthHealth swallows its
					// own errors and resolves to null). If the installed-apps list can't be loaded
					// (apps == null — rejected or non-OK response), we can't reconcile, so we fall back
					// to the raw failing platforms rather than HIDE a genuine reconnect need. Only an
					// actually-loaded apps list is allowed to suppress the banner.
					const [authHealthResult, appsResult] = await Promise.allSettled([getAuthHealth(), getApps()]);
					const authHealthData = authHealthResult.status === "fulfilled" ? authHealthResult.value : null;
					const apps = appsResult.status === "fulfilled" ? appsResult.value : null;
					const rawFailingPlatforms = authHealthData?.failingPlatforms ?? [];
					const connectedFailingPlatforms = resolveReconnectBannerPlatforms(rawFailingPlatforms, apps);

					if (connectedFailingPlatforms.length > 0) {
						this.showExchangeAuthHealthBar = true;
						this.authHealthFailingPlatforms = connectedFailingPlatforms;
					} else {
						if (rawFailingPlatforms.length > 0) {
							// Diagnostic: the endpoint flagged platforms but none map to a connected
							// app — almost always stale Exchange records. Log so the next time the
							// banner "won't go away" we can confirm the filter, not re-investigate.
							console.warn(
								"checkExchangeAuthHealth: suppressing reconnect banner — failing platforms have no matching installed app",
								{
									failingPlatformCodeNames: rawFailingPlatforms.map((platform) => platform?.platformCodeName),
									installedAppCodeNames: (apps || []).filter((app) => app?.isInstalled).map((app) => app?.codeName)
								}
							);
						}
						this.showExchangeAuthHealthBar = false;
						this.authHealthFailingPlatforms = [];
					}
				} catch (error) {
					console.error("Failed to check auth health:", error);
					this.showExchangeAuthHealthBar = false;
					this.authHealthFailingPlatforms = [];
				}
			},
			handleReconnect(platform) {
				this.openReAuthenticationUrl(platform);
			},
			openReAuthenticationUrl(platform) {
				if (!platform.reAuthenticationUrl) {
					// LSP-19467: both fail-conditions in this gate now log with correlatable ids so a
					// reconnect that goes nowhere is triagable from Datadog rather than reconstructed.
					logWarn("App.openReAuthenticationUrl: platform has no reAuthenticationUrl", {
						platformId: platform.platformId ?? null,
						platformCodeName: platform.platformCodeName ?? null,
						platformName: platform.platformName ?? null
					});
					if (this.$refs.exchangeAuthHealthDialog) {
						this.$refs.exchangeAuthHealthDialog.resetAuthenticating();
					}
					return;
				}

				// Open the ReAuthentication URL in a new window
				let reauthWindow = window.open(platform.reAuthenticationUrl, "_blank");
				if (!reauthWindow) {
					// LSP-19467: the re-authentication URL is a credential-bearing link — it belongs in
					// neither the toast nor Datadog. Log which platform failed and why instead.
					logWarn("App.openReAuthenticationUrl: window.open returned null — popup blocked or tab limit reached", {
						platformId: platform.platformId ?? null,
						platformCodeName: platform.platformCodeName ?? null,
						platformName: platform.platformName ?? null
					});
					if (this.$refs.exchangeAuthHealthDialog) {
						this.$refs.exchangeAuthHealthDialog.resetAuthenticating();
					}
					this.toast.error(POPUP_BLOCKED, { timeout: calculateTimeout(POPUP_BLOCKED) });
					return;
				}

				// Watch for window closure and refetch auth health
				watchConnected(reauthWindow, async () => {
					if (this.$refs.exchangeAuthHealthDialog) {
						this.$refs.exchangeAuthHealthDialog.resetAuthenticating();
					}

					await this.checkExchangeAuthHealth();

					if (this.authHealthFailingPlatforms.length === 0) {
						this.showExchangeAuthHealthDialog = false;
					}
				});
			},
			nav: function (url) {
				// Some of the navigation items are direct URLs and some are functions. Checking for 'string' makes sure
				// that only the direct URL navigations are force reloaded. This fixes the reload when the user clicks on 'Billing'
				// to go to the Stripe portal and the page reloads unnecessarily

				if (url && typeof url === "string") {
					let currentName = this.$route.name;
					this.$router.push(url).then(() => {
						if (this.$route.name === currentName) {
							this.forceRerenderKey = !this.forceRerenderKey;
						}
					});
				}
			},
			openContactSupportDialog() {
				this.$refs.contactSupportDialog.open();
			},
			openDataPrivacyDialog() {
				this.$refs.dataPrivacyDialog.open();
			},
			whatsNewEnabled() {
				return isWhatsNewEnabled();
			},
			openGettingStartedGuide() {
				window.open("https://www.liveswitch.com/contact/getting-started-guide");
			},
			showPrivacyPolicy: function () {
				window.open("https://www.liveswitch.com/legal-privacy-policy");
			},
			getProfileItemOrder(itemId) {
				if (itemId === "switch-organization") {
					return 0;
				}
				if (itemId === "users") {
					return 1;
				}
				if (itemId === "settings") {
					return 2;
				}
				if (itemId === "billing") {
					return 4;
				}
				return 3;
			},
			getProfileMenuItems() {
				const profileSlotItems = this.navigationItems.filter((item) => item.slots.includes("profile") && this.isItemVisible(item));
				let overflowItems = [];
				if (this.display.smAndDown.value) {
					overflowItems = this.navigationItems.filter((item) => {
						if (!item.slots.includes("left")) {
							return false;
						}
						if (item.slots.includes("profile")) {
							return false;
						}
						if (item.showInBottom) {
							return false;
						}
						if (item.mobileExcluded && this.display.smAndDown.value) {
							return false;
						}
						return this.isItemVisible(item);
					});
				}
				return [...profileSlotItems, ...overflowItems];
			},
			isItemVisible(item) {
				return this.shouldShow(item.permission) && (item.shouldShow ? item.shouldShow() : true);
			},
			resolveItemTo(item) {
				if (typeof item.to === "function") {
					return item.to() || "";
				}
				return item.to || "";
			},
			navItemActive(item) {
				return !item.locked?.() && this.isNavItemActive(item);
			},
			navItemTo(item) {
				return item.locked?.() ? undefined : this.resolveItemTo(item);
			},
			navItemClick(item) {
				this.helpMenuOpen = false;
				if (item.locked?.()) {
					this.openUpgradeDialog();
				}
			},
			profileItemClick(item) {
				if (item.locked?.()) {
					this.openUpgradeDialog();
				} else if (item.onClick) {
					item.onClick();
				} else {
					this.nav(this.resolveItemTo(item));
				}
			},
			shouldShow: function (sectionName) {
				// no account = show nothing
				if (!this.account) {
					return false;
				}

				let hasPermission = false;
				if (sectionName === "") {
					// basically for the "show all" home only
					hasPermission = true;
				} else {
					// can they read this entity?
					hasPermission = this.account.hasPermissionTo("READ_" + sectionName);
				}
				// make sure we have an organization - otherwise we're still setting up
				const hasOrganization = this.anyOrganizations();

				// HACK ALERT!!!
				// this is garbage, do not ever ever ever do this
				// we killed the inbound functionality for new customers,
				// so all legacy customers got this flag set
				// and they are the only ones that get to see the "website" section, for inbound
				// message handling
				if (hasOrganization && sectionName === "WEBSITE" && !this.me.organization.supportsInboundMessaging) {
					hasPermission = false;
				}

				return hasPermission && hasOrganization;
			},
			showBottomNav() {
				if (this.isKeyboardVisibleInLandscape) {
					return false;
				}
				return this.display.smAndDown.value;
			},
			isLandscapeViewport() {
				return window.screen.orientation
					? window.screen.orientation.type.startsWith("landscape")
					: window.matchMedia("(orientation: landscape)").matches;
			},
			handleVisualViewportResize() {
				const viewport = window.visualViewport;
				if (!viewport) return;
				const isLandscape = this.isLandscapeViewport();

				if (!isLandscape) {
					this.landscapeBaselineHeight = null;
					this.isKeyboardVisibleInLandscape = false;
					return;
				}

				if (
					shouldTrustLandscapeViewportBaseline({
						isLandscape,
						innerHeight: window.innerHeight,
						visualViewportHeight: viewport.height
					})
				) {
					this.landscapeBaselineHeight = getLandscapeViewportBaseline({
						innerHeight: window.innerHeight,
						visualViewportHeight: viewport.height
					});
				}

				this.isKeyboardVisibleInLandscape = isLandscapeKeyboardVisible({
					isLandscape,
					innerHeight: window.innerHeight,
					landscapeBaselineHeight: this.landscapeBaselineHeight,
					visualViewportHeight: viewport.height
				});
			},
			updateLandscapeBaseline() {
				this.landscapeBaselineHeight = null;
				this.handleVisualViewportResize();
			},
			showLeftNav() {
				return !this.display.smAndDown.value;
			},
			closeHelpMenu() {
				this.helpMenuOpen = false;
			},
			showReferralPage() {
				this.showReferralDialog = true;
			},
			showHeresWhatsComingPage() {
				window.open("https://www.liveswitch.com/listening-to-you");
			},
			logout: function () {
				this.$auth.doDefaultLogout();
			},
			syncSocketAuth(me) {
				if (!me?.user?.id || !me?.organization?.id) {
					return;
				}
				const nextAuth = {
					userId: me.user.id,
					organizationId: me.organization.id
				};
				const currentAuth = Socket.auth || {};
				const authChanged = currentAuth.userId !== nextAuth.userId || currentAuth.organizationId !== nextAuth.organizationId;
				Socket.auth = nextAuth;
				if (Socket.connected && authChanged) {
					Socket.disconnect();
					Socket.connect();
					return;
				}
				if (!Socket.connected) {
					Socket.connect();
				}
			},
			async refreshCurrentUserContextForOrgSwitch() {
				// Stale-resolution guard: if a user fires two rapid switches
				// (A→B then B→C), both watcher callbacks invoke this method.
				// Without the request scope, B's slower refreshMe could resolve
				// AFTER C's, overwriting this.me with B's data while the token
				// is for C — scribe + socket would see mismatched state.
				const { requestId } = this.orgSwitchRequestScope.startRequest();

				// Phase 1 — *critical* state that must reflect the new org before
				// we touch anything that talks to the network on the user's
				// behalf. The socket auth in particular is load-bearing: until
				// we reconnect with the new org's credentials, scribe / realtime
				// keep delivering events for the previous org. If any of the
				// optional reload work below throws, the socket has already
				// caught up.
				//
				// useOrgRouteGuard already calls api.invalidateMe() on the same
				// org-id change. refreshMe also nulls _me/_mePromise before
				// fetching, so we don't redundantly invalidate here — the
				// fetch is authoritative either way.
				try {
					const me = await api.refreshMe();
					if (!this.orgSwitchRequestScope.isCurrent(requestId)) {
						return;
					}
					this.me = me;
					await this.storeUserAccount();
					if (!this.orgSwitchRequestScope.isCurrent(requestId)) {
						return;
					}
					this.allowSettingsPage = !hasFreeTrialEnded(this.me.organization, this.me.organizationSubscription);
					this.syncSocketAuth(this.me);
				} catch (error) {
					if (!this.orgSwitchRequestScope.isCurrent(requestId)) {
						// Stale failure — a newer switch is already in flight.
						// Don't toast; the newer one will own the UX.
						return;
					}
					logError("App: failed to refresh user context after org switch", error);
					this.toast.error("We couldn't finish switching organizations. Please refresh and try again.");
					return;
				}

				// Phase 2 — non-essential refresh work. Failures here log but
				// don't undo the org switch. Each is independently try/caught
				// so one slow/broken endpoint can't starve the others.
				try {
					await this.getNotifications();
					if (!this.orgSwitchRequestScope.isCurrent(requestId)) return;
				} catch (error) {
					if (this.orgSwitchRequestScope.isCurrent(requestId)) {
						logError("App: getNotifications failed after org switch", error);
					}
				}
				try {
					this.possiblyShowPluginInstallations();
				} catch (error) {
					if (this.orgSwitchRequestScope.isCurrent(requestId)) {
						logError("App: possiblyShowPluginInstallations failed after org switch", error);
					}
				}
				// LSP-15861: an org switch while already sitting on /integrations does not fire
				// the "$route.name" watcher, so without this the once-per-session prompt could
				// show but the "dismissed" state would never be acknowledged (badge lingers).
				// The method no-ops unless the freshly-loaded me state is exactly "dismissed".
				try {
					if (this.$route?.name === "integrations") {
						// Bugbot (PR #2980, M6): this call was previously un-awaited — since
						// acknowledgePluginInstallBannerIfDismissed is async, any rejection became a
						// promise rejection AFTER this call already returned, escaping this catch
						// entirely rather than being caught by it. Await it so a rejection is actually
						// caught here.
						await this.acknowledgePluginInstallBannerIfDismissed();
					}
				} catch (error) {
					if (this.orgSwitchRequestScope.isCurrent(requestId)) {
						logError("App: acknowledgePluginInstallBannerIfDismissed failed after org switch", error);
					}
				}
				try {
					await this.checkExchangeAuthHealth();
				} catch (error) {
					if (this.orgSwitchRequestScope.isCurrent(requestId)) {
						logError("App: checkExchangeAuthHealth failed after org switch", error);
					}
				}
			},
			reauth: function () {
				// Triggered from the session-lost banner. Drop the broken token
				// state and send the user back through Auth0 — almost always
				// recovers without them having to sign out first.
				// EXPLICIT: the user clicked the banner, so this must not be silently dropped
				// (LSP-17342).
				this.$auth.doDefaultLogin({ isExplicitUserIntent: true });
			},
			anyOrganizations: function () {
				return this.organizations().length > 0;
			},
			organizations: function () {
				return this.me && this.me.organizations ? this.me.organizations : [];
			},
			switchToOrg: async function (org) {
				const ok = await this.orgSwitch.switchOrganization(org.id);
				if (!ok) {
					this.toast.error(couldNot("switch organizations"));
				}
			},
			async startUploadResumePolling() {
				// The small-file lane is independent of the multipart feature flag. It is the
				// reload/retry host for snapshot extraction, annotation, and rotation rows.
				const rearmSmallFileUploads = () => {
					try {
						getAdminSmallFileUploadLane(api).rearm();
					} catch (error) {
						console.warn("[Upload] Admin small-file recovery host unavailable", error);
					}
				};
				rearmSmallFileUploads();
				if (this.$route.name === "devtools-uploads") return;
				if (this._uploadResumeInterval || this._onlineResumeHandler || this._uploadResumePollingStarting) return;

				this._uploadResumePollingStarting = true;
				try {
					this._uploadDrain = createAdminUploadDrain(this, rearmSmallFileUploads);
					await this._uploadDrain.start();
				} finally {
					this._uploadResumePollingStarting = false;
				}
			},
			stopUploadResumePolling() {
				this._uploadDrain?.dispose();
				this._uploadDrain = null;
			},
			/**
			 * LSP-19384: the user pressed Try again on an upload the app had stopped retrying. The durable
			 * row is already back to `pending` and due immediately; this is what makes a pass actually
			 * happen.
			 *
			 * It has to be asked for. The drain arms its next wake from a numeric `retryAt`, and the
			 * re-arm deliberately sets `retryAt: null` so the attempt is not held behind the old backoff
			 * window — so nothing schedules it. A tray holding only parked rows has no pending work
			 * either, so the drain is very likely asleep rather than merely between passes.
			 *
			 * Diagnostic on failure rather than silence (root CLAUDE.md, silent-failure rule): if this is
			 * the call that does not happen, the symptom is "Try again does nothing", which is exactly
			 * the complaint this ticket exists to fix and must not be invisible a second time.
			 */
			onParkedUploadRearmed(sessionId) {
				const drain = this._uploadDrain;
				if (!drain) {
					console.warn("[Upload] Re-armed a parked upload with no upload drain running; it waits for the next start", {
						sessionId
					});
					return;
				}
				void drain.drainNow("parked-upload-retry").catch((error) => {
					console.warn("[Upload] Could not start a drain pass after the user retried a parked upload", {
						sessionId,
						error
					});
				});
			},
			async resumeMultipartSession(session, onProgress, cancelSignal) {
				return resumeAdminMultipartSession(session, onProgress, cancelSignal);
			},
			async navigateToSubscription() {
				if (this.isNewSubscription) {
					const redirected = await this.navigateToBillingPortal(true);

					if (!redirected) {
						window.location = `${window.env.VITE_STRIPE_URL}?prefilled_email=${this.me.user.email}`;
					}
				} else if (this.isSubscriptionActive) {
					window.location = `${window.env.VITE_STRIPE_URL}?prefilled_email=${this.me.user.email}`;
				} else {
					return this.subscriptionUrl;
				}
			}
		}
	};
</script>

<style lang="scss" scoped>
	.notifications-dropdown {
		max-height: 400px;
		overflow-y: auto;
	}

	.notification-actions-left,
	.notification-actions-right {
		flex: 1;
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100%;
	}
	.notification-actions-left .v-btn,
	.notification-actions-right .v-btn {
		width: 100%;
		height: 100%;
		min-height: 48px;
		padding-left: 0 !important;
		padding-right: 0 !important;
		border-radius: 0;
	}
	.notification-actions-divider {
		display: flex;
		align-items: flex-end;
		height: 100%;
	}
	.notification-actions-divider .v-divider {
		height: 30px;
		align-self: flex-end;
	}
	.banner-text {
		background-color: #ff0000;
		color: #ffffff;
		font-size: 0.625rem;
		position: relative;
		transform: translateY(-0.125rem);
		display: inline-flex;
		align-items: center;
		height: 1rem;
		padding: 0 0.25rem;
		line-height: 1;
		font-weight: 900;
	}

	.nav-item-with-subtext {
		position: relative;
		display: flex;
		flex-direction: column;
		line-height: 1.2;
	}

	.nav-main-text {
		font-weight: 500;
		line-height: 1.2;
		display: flex;
		align-items: center;
	}

	.nav-sub-text {
		font-size: 0.65rem;
		font-weight: 400;
		opacity: 0.8;
		line-height: 1.2;
		margin-top: 1px;
	}

	.nav-banner-centered {
		position: relative !important;
		display: inline-flex !important;
		margin-left: 0.5rem !important;
		align-self: center;
		top: 0.125rem;
		transform: none !important;
	}

	.nav-item-single {
		display: flex;
		align-items: center;
	}
	.nav-item-locked {
		opacity: 0.5;
		cursor: pointer;
	}
	.tab-text {
		padding: 0.125rem 0.25rem;
		border-radius: 0.125rem;
		margin-left: 0.25rem;
	}
	.max-width-300 {
		max-width: 340px !important;
		width: 80%;
	}
	nav .v-icon {
		font-size: 18px !important;
		width: 18px !important;
		height: 18px !important;
	}
	nav .v-icon i {
		padding-right: 8px;
	}
	.action {
		cursor: pointer;
	}
	.main {
		background-color: #fff;
		padding-top: var(--shell-header-height, 64px) !important;
		z-index: 0;
	}

	.shell-banners {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		display: flex;
		flex-direction: column;
		z-index: 1100;
	}

	.shell-banners > .v-app-bar {
		position: relative !important;
		top: auto !important;
		left: auto !important;
		right: auto !important;
		width: 100% !important;
	}

	.session-lost-banner {
		position: fixed !important;
		top: var(--shell-chrome-height, 64px) !important;
		left: 0;
		right: 0;
		z-index: 1085;
		margin: 12px 12px 0 !important;
	}

	.app-top-bar {
		position: fixed !important;
		top: var(--shell-banner-height, 0px) !important;
		left: 0 !important;
		right: 0 !important;
		width: 100% !important;
		transform: none !important;
		visibility: visible !important;
		z-index: 1090 !important;
		border-bottom: 1px solid #e5e7eb;
	}

	// Extra space belongs below the input; centering the taller search would shift it.
	.app-top-bar-expanded :deep(.v-toolbar__content) {
		align-items: flex-start;
		padding-top: 12px;
	}
	.app-top-bar-expanded .app-top-bar-brand {
		padding-top: 2px;
	}

	.primary-nav {
		position: fixed !important;
		top: var(--shell-header-height, 64px) !important;
		bottom: 0 !important;
		height: auto !important;
		transform: none !important;
		visibility: visible !important;
		z-index: 1080 !important;
	}

	.app-top-bar-brand {
		display: flex;
		align-items: center;
	}

	.app-top-bar-brand a {
		display: inline-flex;
		align-items: center;
	}

	.app-top-bar-actions {
		margin-left: auto;
		flex-shrink: 1;
		min-width: 0;

		&:has(.expanded) {
			flex-grow: 1;
		}
	}

	.v-list-item {
		margin: 0 8px;
		padding: 0 8px;
		border-radius: 8px;
	}

	.help-menu-header,
	.help-menu-item {
		user-select: none;
	}

	.v-list a.v-list-item--active {
		color: inherit;
	}

	.v-list-item.v-list-item--active {
		background-color: var(--v-accent-base);
	}

	.profile-menu {
		margin-top: 12px;
		contain: initial;
		overflow: visible;
		border-radius: 8px;
		z-index: 100 !important;
	}
	.profile-menu .v-list,
	.profile-menu-submenu,
	.profile-menu-submenu .v-list {
		border-radius: 8px;
	}
	.profile-menu .theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
		margin: 0px;
		border-radius: 0px;
	}

	.profile-menu-submenu .theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
		margin: 0px;
		padding-left: 15px;
		border-radius: 0px;
	}

	.profile-menu .theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled):hover,
	.profile-menu-submenu .theme--light.v-list-item:not(.v-list-item--active):not(.v-list-item--disabled):hover {
		background-color: #eff3fd;
	}

	.v-list-item a {
		text-decoration: none;
		color: inherit;
	}

	.app-container.shell.bottom-back-buttons::v-deep .sticky-actions > button:first-child {
		border: 1px solid #e0e0e0 !important;
	}

	.menu-footer {
		display: flex;
		flex-direction: row;
	}

	.menu-footer-links {
		display: flex;
		justify-content: space-around;
		padding-top: 8px;
	}

	.menu-footer-links button.v-btn {
		padding: 0 8px;
		font-size: 12px;
		height: 28px;
		border-radius: 4px;
		background-color: #fff;
	}

	.menu-footer-links button.v-btn:hover {
		background-color: #eff3fd;
	}
	.menu-footer-links button.v-btn:before {
		background-color: #fff;
	}

	.v-card {
		text-align: left !important;
	}

	.menu-footer > .v-list-item.v-list-item--link.theme--light,
	.menu-footer > .v-list-item.v-list-item--link.theme--light > .v-list-item__icon > i {
		color: rgba(45, 116, 255, 1);
	}

	.v-application::v-deep
		.v-tabs:not(.v-tabs--vertical):not(.v-tabs--right)
		> .v-slide-group--is-overflowing.v-tabs-bar--is-mobile:not(.v-slide-group--has-affixes)
		.v-slide-group__prev {
		display: none;
	}

	.not-allowed-profile-item {
		background-color: rgb(3, 1, 29, 0.08);
		pointer-events: none;
	}

	.not-allowed-profile-item::v-deep .v-list-item__title,
	.not-allowed-profile-item::v-deep .v-list-item__icon i {
		opacity: 0.3;
	}

	.progress-icon:hover::before,
	.progress-icon:focus::before {
		opacity: 0 !important;
	}

	#more-notifications-link {
		color: #295bbd;
		text-decoration: underline;
		cursor: pointer;
	}
	#more-notifications-link:hover {
		color: #1e4a8f;
		text-decoration: none;
	}

	@media #{map.get($display-breakpoints, 'sm-and-down')} {
		.app-container.shell.bottom-back-buttons {
			background-color: #fff;
		}

		.app-container.shell.bottom-back-buttons:has(.sticky-actions) {
			padding-bottom: calc(56px + env(safe-area-inset-bottom, 0px));
		}

		.app-container.shell.bottom-back-buttons::v-deep .sticky-actions {
			position: fixed;
			bottom: calc(55px + env(safe-area-inset-bottom, 0px));
			display: flex;
			justify-content: space-evenly;
			margin-left: -28px;
			width: 100%;
			background-color: white;
			box-shadow:
				0px -2px 4px -1px rgba(0, 0, 0, 0.2),
				0px -4px 5px 0px rgba(0, 0, 0, 0.14);
			z-index: 5;
		}

		.app-container.shell.bottom-back-buttons::v-deep .sticky-actions > button {
			width: 35%;
		}

		.app-container.shell.bottom-back-buttons::v-deep:has(.contact-page) {
			padding-left: 0px;
			padding-right: 0px;
		}
		.app-container.shell.bottom-back-buttons::v-deep:has(.artifact-page) {
			background-color: #fff;
		}
		.app-container.shell::v-deep:has(.artifact-page) {
			background-color: #fff;
		}
	}

	.embeded .app-container.shell {
		padding: 0;
		border-radius: 0;
	}
	.embeded .app-container.shell .page {
		border-radius: 0;
	}

	.v-main::v-deep .v-data-table > .v-data-table__wrapper tbody tr:first-child:hover td:last-child {
		border-top-right-radius: 0;
	}
	.v-main::v-deep .v-data-table > .v-data-table__wrapper tbody tr:first-child:hover td:first-child {
		border-top-left-radius: 0;
	}
</style>
<style lang="scss" scoped>
	.maintenance::v-deep .v-snack__wrapper {
		border-left-width: 8px;
		border-left-style: solid;
	}

	.maintenance::v-deep .maintenance-content h3 {
		font-size: 1.5rem;
	}

	.maintenance::v-deep .maintenance-content {
		position: relative;
		padding-right: 32px;
	}

	.maintenance::v-deep .maintenance-close {
		position: absolute;
		top: -8px;
		right: -8px;
		font-size: 12px;
	}

	.maintenance::v-deep .v-snackbar__wrapper.primary .maintenance-close {
		color: #0070ff !important;
	}
	.maintenance::v-deep .v-snackbar__wrapper.error .maintenance-close {
		color: #92400e !important;
	}

	.maintenance::v-deep .v-snack__wrapper.primary {
		border-left-color: #0070ff !important;
		background-color: #e8f1fe !important;
		color: #0070ff !important;
	}

	.maintenance::v-deep .v-snack__wrapper.error {
		border-left-color: #b45309 !important;
		background-color: #fef3c7 !important;
		color: #92400e !important;
	}

	.app-container.shell {
		padding-top: 0;
	}

	// @media (min-width: 960px) {
	.app-container.shell.no-padding {
		padding: 0 !important;
	}

	/* Existing Sparks decide whether to mount the embedded editor only after
	 * their pack metadata loads. `:has` preserves the full-width editor once it
	 * is present, while Configure Knowledge keeps the standard page padding. */
	.app-container.shell::v-deep:has(.advanced-editor-page) {
		padding: 0 !important;
	}
	// }

	.v-system-bar.subscription {
		text-align: center;
		text-transform: uppercase;
		background-color: var(--v-primary-base);
		height: 52px !important;
		z-index: 5;
	}

	@media (max-width: 600px) {
		.v-system-bar.subscription {
			font-size: 0.6rem;
		}
	}

	.avatar {
		font-size: 1rem !important;
	}

	.profile-user-details:hover {
		background-color: #fff !important;
	}

	.profile-user-details :deep(.v-list-item__prepend) {
		display: block;
		margin-right: 16px;
	}

	.profile-user-details :deep(.v-list-item__content) {
		min-width: 0;
	}

	.profile-name {
		font-size: small;
		max-width: 150px !important;
		padding-top: 3px;
	}

	.profile-activator {
		max-width: 320px;
	}

	.profile-summary {
		min-width: 0;
		max-width: 190px;
		line-height: 1.1;
	}

	.profile-summary-name {
		font-size: 0.875rem;
		font-weight: 600;
		color: rgba(0, 0, 0, 0.87);
	}

	.profile-summary-org {
		font-size: 0.75rem;
		color: rgba(0, 0, 0, 0.6);
		margin-top: 2px;
	}

	.profile-top-avatar {
		flex-shrink: 0;
	}

	.profile-top-avatar-text {
		font-size: 0.875rem !important;
		font-weight: 700;
		line-height: 1;
	}

	.profile-expand-icon {
		color: rgba(0, 0, 0, 0.6);
	}

	.app-container.mobile-container-app {
		padding-left: 12px;
		padding-right: 12px;
	}

	/* iPhone notch / Dynamic Island safe-area handling for landscape mode */
	@media (min-width: 320px) and #{map.get($display-breakpoints, 'sm-and-down')} and (orientation: landscape) {
		header::v-deep > .v-toolbar__content {
			padding-left: max(env(safe-area-inset-left, 48px), 16px);
			padding-right: max(env(safe-area-inset-right, 48px), 16px);
		}

		.app-container.mobile-container-app {
			padding-left: max(calc(env(safe-area-inset-left) - 12px), 12px);
			padding-right: max(calc(env(safe-area-inset-right) - 12px), 12px);
		}

		.app-container.shell {
			border-radius: 0px;
			padding-left: max(calc(env(safe-area-inset-left) - 12px), 12px);
			padding-right: max(calc(env(safe-area-inset-right) - 12px), 12px);
		}

		.app-container.shell.no-padding {
			padding-left: env(safe-area-inset-left) !important;
			padding-right: env(safe-area-inset-right) !important;
		}

		.v-bottom-navigation {
			padding-left: env(safe-area-inset-left);
			padding-right: env(safe-area-inset-right);
			padding-bottom: env(safe-area-inset-bottom);
		}
	}

	/* supports about 5 buttons max; was 80px, or 4 items max */
	.v-item-group.v-bottom-navigation .v-btn {
		min-width: 64px;
	}
	/* Search bar. */
	.search-bar {
		max-width: 100%;
		flex-grow: 0;
		flex-shrink: 1;
		width: 300px;
		min-width: 0;

		@media #{map.get($display-breakpoints, 'md-and-up')} {
			min-width: 300px;
			flex-grow: 1;
		}

		&.expanded {
			@media #{map.get($display-breakpoints, 'sm-and-down')} {
				max-width: 100%;
				flex-grow: 10;
			}
		}
	}
</style>
<style lang="scss">
	/* LSP-16424: the renamed "My Stuff / Sessions" label wraps to two lines in the narrow mobile
	   bottom-nav tab and was left-aligned. Centre the wrapped lines and stretch the button content
	   to the full tab width. Deliberately GLOBAL (not scoped): the label <span> and Vuetify's
	   internal .v-btn__content render without this component's scope attribute, so a scoped rule
	   would not reliably match. Selector intentionally avoids `.v-item-group` (Vuetify 3's
	   v-bottom-navigation root does NOT carry that class) and `.v-btn` so it matches the real DOM;
	   it stays bottom-nav-specific, so the wider desktop sidebar is unaffected. */
	.v-bottom-navigation .v-btn__content {
		width: 100%;
	}
	.v-bottom-navigation .v-btn__content > span {
		display: block;
		width: 100%;
		text-align: center;
		white-space: normal;
		line-height: 1.15;
	}
	/* LSP-21614: style.css grows the bottom nav by env(safe-area-inset-bottom) (the page is
	   viewport-fit=cover), but Vuetify only reserves the nav's 56px `height` under v-main. On a phone
	   with a gesture/nav-bar inset, the last strip of every page sat behind the nav — the quick-action
	   pages (Start/Schedule Live Call, Request Stuff, Start a Recording) end in their Cancel/Send row,
	   so those buttons were cut in half. Reserve the inset too. The sibling selector only matches while
	   the nav is rendered (it is v-if'd off on desktop and with the landscape keyboard up). */
	.v-bottom-navigation ~ .v-main {
		padding-bottom: calc(var(--v-layout-bottom) + env(safe-area-inset-bottom, 0px));
	}
	#app.embeded .project-page .add-item-button,
	#app.embeded .conversation-page .add-item-button {
		display: none !important;
	}
	#app.embeded .project-page .page-actions,
	#app.embeded .conversation-page .page-actions {
		display: none !important;
	}
	#app.embeded #fab-button {
		display: none !important;
	}
	.text-truncate::after {
		content: "";
		display: block;
	}
	.profile-avatar {
		font-size: x-small !important; /* Avatar is smaller than the default */
	}

	textarea.v-field__input {
		color: black !important;
	}
	input.v-field__input {
		color: black !important;
	}
	.error--text div.v-messages__message {
		color: red;
	}
	.v-progress-linear {
		color: #2d74ff !important;
	}
	.v-data-table-progress {
		background-color: white !important;
	}
	.v-card-text {
		padding: 0.5rem !important;
	}
	.v-list-item--variant-text .v-list-item__overlay {
		background-color: white !important;
	}
	@media (hover: none) {
		.search-filter .v-list-item:hover:not(.selected-row) {
			background-color: white !important;
		}
	}
	.selected-row .v-list-item__prepend {
		color: #2d74ff !important;
	}
	.selected-row .v-selection-control--disabled {
		opacity: 1 !important;
	}

	@media #{map.get($display-breakpoints, 'md-and-up')} {
		.entity-tab.v-btn.v-tab {
			color: #000 !important;
			background-color: #fff !important;
		}
		.entity-tab.v-btn.v-tab.v-tab--selected {
			color: #000 !important;
			background-color: rgb(243, 245, 249) !important;
		}
	}
	.tabs-container-wrapper {
		border-bottom: 2px solid #f3f4f5 !important;
	}
	.v-switch__track.bg-primary {
		opacity: 1 !important;
	}
	.v-switch .v-label {
		color: rgba(0, 0, 0, 0.54) !important;
	}
	.v-card-title {
		hyphens: none !important;
	}
	.v-counter.text-error {
		padding-left: 4px !important;
	}

	.v-navigation-drawer.primary-nav {
		background-color: color-mix(in oklab, var(--color-gray-50) 50%, transparent) !important;
	}

	.v-navigation-drawer .v-list-item--active {
		background-color: rgb(202, 215, 248) !important;
	}
	.v-navigation-drawer .nav-item-conversations.v-list-item--active {
		background-color: oklch(0.546 0.245 262.881 / 0.2) !important;
	}
	.v-navigation-drawer .nav-item-projects.v-list-item--active {
		background-color: oklch(0.596 0.145 163.225 / 0.2) !important;
	}
	.v-navigation-drawer .nav-item-contacts.v-list-item--active {
		background-color: oklch(0.65 0.15 350 / 0.2) !important;
	}
	.v-navigation-drawer .nav-item-forms.v-list-item--active {
		background-color: oklch(0.75 0.18 85 / 0.2) !important;
	}
	.v-navigation-drawer .nav-item-reports.v-list-item--active {
		background-color: oklch(0.558 0.223 306 / 0.2) !important;
	}
	.v-navigation-drawer .v-list-item--active * {
		color: #000 !important;
	}
	.v-navigation-drawer .v-list-item--nav .v-list-item__prepend {
		color: rgba(0, 0, 0, 0.54) !important;
	}
	.v-date-picker-month__days .text-primary {
		color: #000 !important;
		border-radius: 10px !important;
		border-color: #000 !important;
	}
	.conversation-card .v-btn--variant-outlined {
		border-color: rgb(224, 224, 224) !important;
	}
	.v-application .mobile-items-per-page * {
		caret-color: transparent !important;
	}
	.speed-list .acitve {
		border-left: 2px solid #2d74ff !important;
	}
	.mobile-container-app .v-card:not(.rounded-xl) {
		border-radius: 0px !important;
	}
	.help-menu-list {
		--v-list-prepend-gap: 8px;
	}

	@media print {
		.no-print {
			display: none !important;
		}

		.v-navigation-drawer {
			display: none !important;
			width: 0 !important;
		}

		.v-app-bar {
			display: none !important;
			height: 0 !important;
		}

		.v-layout {
			padding-left: 0 !important;
			padding-top: 0 !important;
		}

		.v-main {
			padding: 0 !important;
		}

		.v-main.main {
			padding: 0 !important;
		}

		.app-container {
			padding: 0 !important;
			margin: 0 !important;
			max-width: none !important;
		}

		/* Hide teleported overlays. Vuetify (.v-overlay-container) and vue-toastification
		   (.Vue-Toastification__container) mount at document.body, outside any route
		   component's subtree, so scoped @media print rules cannot reach them. Without
		   this, transient pop-ups ("Sheet saved.", errors, etc.) leak into the customer's
		   print preview. */
		.Vue-Toastification__container,
		.v-overlay-container {
			display: none !important;
		}

		/* LSP-16402: WebKit (desktop Safari 26 + iOS Safari) clips printed output to a
		   single viewport-height page when the printed content sits inside ancestors with
		   viewport-locked heights and flex layout (.v-application is flex,
		   .v-application__wrap is min-height: 100vh flex column, and the authenticated
		   v-container is fill-height/height: 100%). The signed-OUT sheet route renders
		   SheetView outside this container chain and prints fine; the signed-IN route
		   wraps it in the chain, so prints came out blank/truncated on Safari only.
		   Relax the chain to natural block flow during print. Gated with
		   :has(.sheet-view-page) so other print paths (e.g. the Scribe report print
		   clone) keep today's layout. */
		.v-application:has(.sheet-view-page),
		.v-application:has(.sheet-view-page) .v-application__wrap,
		.v-application:has(.sheet-view-page) .v-main,
		.v-application:has(.sheet-view-page) .app-container.shell {
			display: block !important;
			height: auto !important;
			min-height: 0 !important;
			overflow: visible !important;
		}
	}
</style>
